/**
 * Stocktake detail page — `/stock/stocktakes/:id` (§8.3 – §8.7).
 *
 * Mobile-first counting screen with three operating modes:
 *
 *   in-progress       → counting interface (numpad on mobile, scanner
 *                       listener active, live mismatch chips)
 *   preview-finalize  → modal-like overlay listing the proposed
 *                       Stock-loss expense + adjustment delta +
 *                       blockers (Pfand stays on-site per 2026-06-04)
 *   finalized         → read-only display of snapshot fields with an
 *                       edit-cascade affordance and a "Delete
 *                       stocktake" button that requires typing the id
 *
 * Concurrency: every PATCH/DELETE carries an ``If-Match: <stocktake.
 * updated_at>``. A 412 surfaces as an inline "Someone else edited
 * this — reload?" banner (mirrors ProcurementDetailPage §6.5b).
 *
 * Scanner integration: a global ``keydown`` listener accumulates
 * digits + alphanumeric characters until Enter (or 250ms of idle)
 * fires a single ``POST /scan`` with a fresh client-side UUID
 * idempotency key. The listener is gated by ``e.isTrusted === true``
 * so the in-page Numpad's synthetic clicks do NOT register as scans.
 *
 * Offline detection: a network failure on /scan or PATCH /lines
 * raises a non-dismissable "Connection lost" modal. The scanner
 * listener is paused while the modal is up. Counts already on the
 * server are preserved; on Retry we re-fetch the detail + bootstrap.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Empty,
  Input,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CameraOutlined,
  DownOutlined,
  SearchOutlined,
  UpOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";

import {
  ApiError,
  api,
  type ArticleRow,
  type CategoryRow,
  type ChainRow,
  type FinalizeProposal,
  type ProductGroupRow,
  type ReanchorLineDiffRow,
  type ReanchorPreviewResponse,
  type ScanResponse,
  type StocktakeDetail,
  type StocktakeLineRow,
} from "../../api/client";
import { CountExpressionInline } from "../../components/stock/CountExpressionInline";
import { CountExpressionKeypad } from "../../components/stock/CountExpressionKeypad";
import { Numpad } from "../../components/stock/Numpad";
import { StocktakeOpenLevels } from "../../components/stock/StocktakeOpenLevels";
import { UnknownBarcodeModal } from "../../components/stock/UnknownBarcodeModal";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { formatCents, formatCompactDateTime } from "../../utils/dateFormat";

const { Title, Text } = Typography;

// ---------------------------------------------------------------------------
// BarcodeDetector type shim
// ---------------------------------------------------------------------------
//
// lib.dom doesn't include BarcodeDetector yet (Chrome-Android only). We
// declare just enough surface area to call detect() against a video
// element. The cameraSupported guard below feature-detects via
// ``"BarcodeDetector" in window`` so non-supporting browsers never even
// render the toggle.

interface BarcodeDetectorResult {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: HTMLVideoElement): Promise<BarcodeDetectorResult[]>;
}
interface BarcodeDetectorConstructor {
  new (options?: { formats?: string[] }): BarcodeDetectorLike;
}
declare global {
  interface Window {
    BarcodeDetector?: BarcodeDetectorConstructor;
  }
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

/** crypto.randomUUID() is widely-shipped (all evergreen browsers + Node
 * ≥19) but TypeScript's DOM lib in some configs still types it as
 * optional. Fall back to Math.random when it's missing — collision is
 * fine here since the server pairs (stocktake_id, idempotency_key) and
 * a 36^16 space is plenty for a single session. */
function newUuid(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return (
    Math.random().toString(36).slice(2, 18) +
    Math.random().toString(36).slice(2, 18)
  );
}

/** Subset of ApiError predicate from ProcurementDetailPage. */
function isIfMatchMismatch(err: unknown): boolean {
  return err instanceof ApiError && err.status === 412;
}

function isNetworkError(err: unknown): boolean {
  // ApiError carries an HTTP status; a fetch that never reached the
  // server throws a TypeError (in browsers). That's our "offline"
  // signal.
  return !(err instanceof ApiError) && err instanceof TypeError;
}

/** Tiny WebAudio beep (~880Hz, 80ms) — used on successful scan.
 *
 * Honours ``prefers-reduced-motion`` the same way ``vibrate()`` does:
 * a user who has opted out of motion is also asking for "fewer ambient
 * sensory pings", so we stay silent. The ``feedbackEnabled`` switch is
 * the operator's explicit override and lives separately at the call
 * site — it's "I want feedback right now"; this is "the OS says no
 * background pings". Both gates apply. */
function playBeep() {
  try {
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    type WindowWithWebkit = Window & {
      webkitAudioContext?: typeof AudioContext;
    };
    const w = window as WindowWithWebkit;
    const Ctx = window.AudioContext ?? w.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880;
    gain.gain.value = 0.15;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.08);
    // Close the context shortly after the beep so we don't leak a node
    // graph per scan.
    setTimeout(() => void ctx.close().catch(() => undefined), 200);
  } catch {
    // No-op — audio is best-effort.
  }
}

/** Vibration API haptic — respects prefers-reduced-motion. */
function vibrate() {
  try {
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.vibrate === "function"
    ) {
      navigator.vibrate(30);
    }
  } catch {
    // No-op.
  }
}

// ---------------------------------------------------------------------------
// Local state shape
// ---------------------------------------------------------------------------

interface ExpectedSnapshot {
  /** chain_root_id → expected_qty_at_start (server snapshot anchored
   * at each line's first-stamp ``counted_at``). */
  byRoot: Map<number, number>;
  /** chain_root_id → expected_qty_now (server snapshot anchored at
   * NOW). The drift = nowByRoot − byRoot per chain root. Drives the
   * "↻ N" re-walk affordance. */
  nowByRoot: Map<number, number>;
  snapshotAt: string;
}

interface CountState {
  /** chain_root_id → current counted_qty as known to the client. */
  byRoot: Map<number, number>;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const StocktakeDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const stocktakeId = Number(id);

  // Server data
  const [row, setRow] = useState<StocktakeDetail | null>(null);
  const [articles, setArticles] = useState<ArticleRow[]>([]);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [groups, setGroups] = useState<ProductGroupRow[]>([]);
  // Chain rows carry category/group metadata that ArticleRow doesn't.
  // Fetched best-effort alongside articles for the tree-view grouping.
  const [chains, setChains] = useState<ChainRow[]>([]);
  const [expected, setExpected] = useState<ExpectedSnapshot | null>(null);
  const [counts, setCounts] = useState<CountState>({ byRoot: new Map() });
  const [loading, setLoading] = useState(false);

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [staleBanner, setStaleBanner] = useState(false);
  const [search, setSearch] = useState("");
  const [treeView, setTreeView] = useState(true);
  /** §8.3 minor — the no-barcode section is collapsible. Defaults open
   * so card / loose items are visible from the start; the operator can
   * collapse the panel once those rows are counted. */
  const [noBarcodeOpen, setNoBarcodeOpen] = useState(true);
  /** Hide rows that already have a count, so the operator's list
   * shrinks as they progress. Defaults ON — the activeRoot exception
   * in buildRows keeps the currently-selected row visible regardless. */
  const [hideCounted, setHideCounted] = useState(true);
  /** Active row drives the info-bar (Expected/Counted/Mismatch/=).
   * Bumped on BOTH tap (selectRow) and scan (fireScan) so the operator
   * always sees context for the most recent action. */
  const [activeRoot, setActiveRoot] = useState<number | null>(null);
  /** Selected row drives the LIST highlight only. Bumped ONLY on tap.
   * Scans deliberately don't move the highlight so the operator's
   * visual focus on the no-barcode row they're counting stays put when
   * a USB-wedge scan lands. */
  const [selectedRoot, setSelectedRoot] = useState<number | null>(null);
  const [numpadValue, setNumpadValue] = useState("");
  const [scannerEnabled, setScannerEnabled] = useState(true);
  const [feedbackEnabled, setFeedbackEnabled] = useState(true);
  // 0051: when ON, the mobile bottom-bar uses the CountExpressionKeypad
  // (loose/crates/broken mode buttons + an editable per-kind value that
  // auto-submits to the server on a 1500ms idle). Default ON; operators
  // who prefer plain single-number entry toggle it off via the "Keypad"
  // header switch and get the bare Numpad instead — useful for pure-loose
  // articles where the mode buttons add noise. The desktop chip strip is
  // always on regardless.
  const [expressionKeypadEnabled, setExpressionKeypadEnabled] =
    useState(true);
  /** Mobile keypad collapse — when true the fixed bottom bar shrinks
   * to a thin handle so the operator can see more of the list. Tapping
   * a row auto-expands it (see selectRow). */
  const [keypadCollapsed, setKeypadCollapsed] = useState(false);
  /** Live running total lifted up from the CountExpressionKeypad so the
   * info bar's centered Counted + Mismatch track what's being typed,
   * instead of jumping to the committed value only on each debounced
   * submit. Reset on row change so it falls back to counts.byRoot until
   * the freshly-mounted keypad reports the new row's total. */
  const [keypadLiveCount, setKeypadLiveCount] = useState<number | null>(null);
  /** Chain-root whose desktop chip-strip input is currently focused.
   * Keeps that row visible under "Hide counted" until the operator leaves
   * the field, so pressing Enter to commit a term doesn't make the row
   * vanish mid-entry. Cleared on blur. */
  const [editingRoot, setEditingRoot] = useState<number | null>(null);
  /** Numpad digit layout — calculator (7-8-9 top) vs phone (1-2-3 top).
   * Operator preference; persists in component state only (no need to
   * round-trip to the server for a UI nicety). */
  const [numpadLayout, setNumpadLayout] = useState<"calculator" | "phone">(
    "calculator",
  );

  // Camera scanner (Chrome Android only — feature-detected). Tap to arm
  // opens a Modal with the live video feed and runs the BarcodeDetector
  // API at ~5Hz. The USB-wedge keydown listener stays untouched; the
  // camera is purely an additional input path. scanFlash drives a
  // green/red outline + shake on the modal preview as visual feedback.
  const [cameraOpen, setCameraOpen] = useState(false);
  const [scanFlash, setScanFlash] = useState<"ok" | "unknown" | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const cameraSupported =
    typeof window !== "undefined" && "BarcodeDetector" in window;

  // Article search on the mobile info bar. When open, the AutoComplete
  // replaces the article-name + Expected/Counted/Mismatch block and the
  // Numpad is hidden to make room for the OS keyboard. AutoComplete
  // opens UPWARD so the suggestion list grows away from the keyboard.
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  // Finalize flow
  const [previewBusy, setPreviewBusy] = useState(false);
  const [preview, setPreview] = useState<FinalizeProposal | null>(null);

  // Offline modal
  const [offline, setOffline] = useState<{ retry: () => Promise<void> } | null>(
    null,
  );

  // Unknown-barcode modal — modeled as a QUEUE (FIFO) so concurrent
  // unresolved scans don't clobber each other. The modal renders the
  // head of the queue; resolving / cancelling shifts it off. Without
  // this, a second unresolved scan arriving before the operator
  // resolves the first would silently overwrite the in-flight barcode.
  const [unknownBarcode, setUnknownBarcode] = useState<string[]>([]);

  // Delete / cancel-stocktake modal. The same Modal shell serves both
  // pre-finalize ("cancel-in-progress") and post-finalize
  // ("delete-finalized") flows — only the copy + the
  // ManualEditedExpensesWarning differ. The mode is null when the
  // modal is closed.
  const [deleteMode, setDeleteMode] = useState<
    "cancel-in-progress" | "delete-finalized" | null
  >(null);
  const [deleteText, setDeleteText] = useState("");
  const deleteOpen = deleteMode !== null;

  // Mobile breakpoint — uses the shared useMediaQuery hook so the
  // threshold (≤ 768px) stays consistent with the spec across pages.
  const isMobile = useMediaQuery("(max-width: 768px)");

  /** Local buffer for the comment TextArea so the parent's row state
   * (which re-renders on every count commit) doesn't blow away the
   * operator's in-flight typing. Syncs FROM ``row.comment`` only when
   * the operator isn't currently editing — same discipline as
   * ``RowCountInput``. Commits on blur via commitComment. */
  const [commentDraft, setCommentDraft] = useState<string>("");
  const commentEditingRef = useRef(false);

  // -------------------------------------------------------------------------
  // Fetch + bootstrap
  // -------------------------------------------------------------------------

  const refresh = useCallback(async () => {
    if (!Number.isFinite(stocktakeId)) return;
    setLoading(true);
    setError(null);
    try {
      const [st, arts, cats, grps] = await Promise.all([
        api.getStocktake(stocktakeId),
        api.listArticles({ active: true }),
        api.listCategories(),
        api.listProductGroups(),
      ]);
      setRow(st);
      setArticles(arts);
      setCategories(cats);
      setGroups(grps);
      // Best-effort: chain metadata feeds the tree view. Don't await
      // alongside the bootstrap call above — a slow /articles full-chain
      // response shouldn't gate the counting screen from rendering.
      api.listArticleChains().then(setChains).catch(() => undefined);
      // Seed the counts map from the server lines so re-loads don't
      // wipe pending values out of view.
      const m = new Map<number, number>();
      for (const line of st.lines) {
        m.set(line.article_chain_root_id, line.counted_qty);
      }
      setCounts({ byRoot: m });
      setStaleBanner(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [stocktakeId]);

  const refreshBootstrap = useCallback(async () => {
    if (!Number.isFinite(stocktakeId)) return;
    try {
      const snap = await api.getStocktakeBootstrap(stocktakeId);
      const m = new Map<number, number>();
      for (const [k, v] of Object.entries(snap.expected_qty_at_start)) {
        m.set(Number(k), v);
      }
      const nowM = new Map<number, number>();
      for (const [k, v] of Object.entries(snap.expected_qty_now ?? {})) {
        nowM.set(Number(k), v);
      }
      setExpected({ byRoot: m, nowByRoot: nowM, snapshotAt: snap.snapshot_at });
    } catch (err) {
      // Don't surface — expected-qty is a nicety, not a blocker.
      if (err instanceof ApiError) setError(err.message);
    }
  }, [stocktakeId]);

  useEffect(() => {
    void refresh();
    void refreshBootstrap();
  }, [refresh, refreshBootstrap]);

  // Re-sync the comment buffer from the server's row.comment when we're
  // NOT mid-edit. Without the ref gate, a concurrent count commit (which
  // re-fetches the row and bumps updated_at) would rewrite the operator's
  // partially-typed comment back to the server's value.
  useEffect(() => {
    if (!commentEditingRef.current) {
      setCommentDraft(row?.comment ?? "");
    }
  }, [row?.comment]);

  // Background refresh of the bootstrap snapshot every 60s — keeps
  // mismatch sane during long counting sessions where other operators
  // might be refilling.
  //
  // Pause while: offline (the modal already owns the retry surface and
  // a background failure on top would muddy the error state), the
  // unknown-barcode queue has anything in it (the operator is mid-
  // triage; an expected-qty refresh racing in would silently shift
  // mismatch chips under them), or the stocktake is finalized (the
  // bootstrap snapshot is immutable from the operator's POV — there's
  // nothing to refresh).
  useEffect(() => {
    const t = setInterval(() => {
      if (offline || unknownBarcode.length > 0 || row?.finalized_at != null) {
        return;
      }
      void refreshBootstrap();
    }, 60_000);
    return () => clearInterval(t);
  }, [refreshBootstrap, offline, unknownBarcode, row?.finalized_at]);

  // -------------------------------------------------------------------------
  // Derived: filtered + sorted articles
  // -------------------------------------------------------------------------

  // The Phase-1 ArticleRow shape doesn't include category/group; we
  // map chain_root_id → ChainRow for the tree view via a separate
  // best-effort fetch. For now the flat A-Z view covers the spec
  // baseline; the tree view falls back to flat if metadata is missing.
  // articleByRoot has one entry per chain root (taking the first leaf
  // with that root we see — the catalog tags barcodes etc. on the
  // root row anyway).
  const articleByRoot = useMemo(() => {
    const m = new Map<number, ArticleRow>();
    for (const a of articles) {
      if (!m.has(a.chain_root_id)) m.set(a.chain_root_id, a);
    }
    return m;
  }, [articles]);

  // chain_root_id → {category, group} (best-effort, falls back to the
  // "Uncategorised" / "Ungrouped" buckets when chain metadata hasn't
  // loaded or the row has no taxonomy assigned).
  const metaByRoot = useMemo(() => {
    const m = new Map<number, { category: string; group: string }>();
    for (const c of chains) {
      if (!m.has(c.chain_root_id)) {
        m.set(c.chain_root_id, {
          category: c.category_name ?? "Uncategorised",
          group: c.product_group_name ?? "Ungrouped",
        });
      }
    }
    return m;
  }, [chains]);

  const filteredArticles = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = Array.from(articleByRoot.values());
    if (q) {
      list = list.filter((a) => a.name.toLowerCase().includes(q));
    }
    // A-Z by article name.
    list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [articleByRoot, search]);

  // Split into "no barcode" + "with barcode" groups for §8.3 layout.
  const { noBarcode, withBarcode } = useMemo(() => {
    const a: ArticleRow[] = [];
    const b: ArticleRow[] = [];
    for (const art of filteredArticles) {
      if (art.barcodes.length === 0) a.push(art);
      else b.push(art);
    }
    return { noBarcode: a, withBarcode: b };
  }, [filteredArticles]);

  // 0044: derive revert markers from server-known lines. Used by
  // buildRows to render a "reverted" badge + suppress ↶/× actions on
  // post-finalize ghost rows. MUST live ABOVE the `if (!row) return …`
  // early return below — otherwise the hook count differs between the
  // loading-state render and the loaded render, which breaks Rules of
  // Hooks. (Originally placed after the early return; fix here.)
  const revertedAtByRoot = useMemo(() => {
    const m = new Map<number, string>();
    for (const line of row?.lines ?? []) {
      if (line.reverted_at != null) {
        m.set(line.article_chain_root_id, line.reverted_at);
      }
    }
    return m;
  }, [row?.lines]);

  // 0051: per-row term list from the server's detail response, keyed by
  // chain root id. Drives the desktop chip strip. Null entries (uncounted
  // lines) and empty arrays (counted-as-zero) are both meaningful and
  // distinct from "no entry in the map" (article hasn't surfaced).
  const countExpressionByRoot = useMemo(() => {
    const m = new Map<
      number,
      import("../../utils/countExpression").Term[] | null
    >();
    for (const line of row?.lines ?? []) {
      m.set(line.article_chain_root_id, line.count_expression ?? null);
    }
    return m;
  }, [row?.lines]);

  // Drop the lifted keypad total when the active row changes, so the info
  // bar falls back to the committed value (counts.byRoot) until the freshly
  // mounted keypad reports the new row's live total.
  useEffect(() => {
    setKeypadLiveCount(null);
  }, [activeRoot]);

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------

  const isFinalized = row?.finalized_at != null;
  const editingDisabled = row === null || offline !== null;

  // -------------------------------------------------------------------------
  // Re-anchor (post-finalize snapshot refresh — see backend
  // /api/stocktakes/{id}/reanchor-preview + /reanchor). We poll the
  // preview once per detail load to decide whether to flag stale lines
  // on the FinalizedLinesView. The full modal lazy-fetches on open.
  // -------------------------------------------------------------------------
  const [reanchorOpen, setReanchorOpen] = useState(false);
  const [stalePreview, setStalePreview] = useState<{
    staleRootIds: Set<number>;
  }>({ staleRootIds: new Set() });
  const refreshStalePreview = useCallback(async () => {
    if (!row || row.finalized_at === null) {
      setStalePreview({ staleRootIds: new Set() });
      return;
    }
    try {
      const preview = await api.reanchorStocktakePreview(row.id);
      setStalePreview({
        staleRootIds: new Set(
          preview.stale_lines.map((line) => line.chain_root_id),
        ),
      });
    } catch {
      // Best-effort; the badge just won't show if the preview fails.
      setStalePreview({ staleRootIds: new Set() });
    }
  }, [row]);
  useEffect(() => {
    void refreshStalePreview();
  }, [refreshStalePreview]);

  // Serialise mutations the same way ProcurementDetailPage does so two
  // rapid commits don't trip 412 on the same stale updated_at.
  const mutationChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const runMutation = useCallback(<T,>(fn: () => Promise<T>): Promise<T> => {
    const next = mutationChainRef.current.then(fn, fn);
    mutationChainRef.current = next.catch(() => undefined);
    return next;
  }, []);

  // Audit-fix MINOR #8: every PATCH/DELETE/POST handler reads
  // ``row?.updated_at`` to set the ``If-Match`` token. The closures
  // capture ``row`` at handler-creation time, so when two clicks
  // queue in the same React tick BOTH closures use the same
  // pre-mutation token. The first PATCH bumps server updated_at →
  // the second 412s with a spurious staleBanner even though the two
  // ops were meant to chain. ``rowRef`` exposes the always-latest
  // committed value so chained execs see each other's
  // ``setRow(fresh)``.
  const rowRef = useRef<StocktakeDetail | null>(null);
  useEffect(() => {
    rowRef.current = row;
  }, [row]);

  const handleMutationError = useCallback(
    (err: unknown, retryFn: () => Promise<void>): boolean => {
      if (isIfMatchMismatch(err)) {
        setStaleBanner(true);
        return true;
      }
      if (isNetworkError(err)) {
        setOffline({ retry: retryFn });
        return true;
      }
      return false;
    },
    [],
  );

  /** Commit ``counted_qty`` for ``chainRootId`` (replace semantics).
   *
   * Concurrency contract: the PATCH carries the locally-known
   * ``stocktake.updated_at`` as ``If-Match``. The mutation queue
   * serialises commits so each PATCH carries the LAST-COMMITTED
   * updated_at (refreshed from the server's response, not a fresh
   * GET). Re-fetching updated_at before the PATCH would defeat the
   * point of If-Match — a parallel operator's edit on the same
   * stocktake would silently land first and ours would still
   * succeed, masking the conflict. The 412 path (handleMutationError
   * → staleBanner) is exactly what the spec asks for.
   *
   * Local optimistic ``counts`` is rolled back if the network throws
   * (so the offline modal doesn't leave drift between what the screen
   * shows and what's on the server). */
  const commitCount = useCallback(
    (
      chainRootId: number,
      value: number,
      /** ``true`` for the "↻ re-walk" affordance: force
       * ``line.counted_at = NOW`` on the server alongside the qty
       * update. Default ``false`` matches the existing typo-fix path. */
      restampCountedAt = false,
      /** 0051: optional structured term list to send alongside the
       * counted_qty. Set from the desktop chip strip; ``undefined``
       * matches legacy clients + the kiosk scanner path (backend
       * drift trigger nulls the column; lazy synthesis on next
       * read). */
      countExpressionOverride?:
        | import("../../utils/countExpression").Term[]
        | null,
    ): Promise<void> =>
      runMutation(async () => {
        if (!Number.isFinite(value) || value < 0) return;
        if (!Number.isFinite(stocktakeId)) return;
        // Snapshot the pre-mutation count so an offline / 412 path
        // can restore it without leaving the row showing a value the
        // server doesn't have.
        const prevForRoot = counts.byRoot.get(chainRootId);
        const exec = async () => {
          // Audit-fix MINOR #8: prefer ``rowRef.current`` over the
          // captured ``row`` closure so a chained mutation picks up
          // the prior step's ``setRow`` synchronously.
          const knownUpdatedAt =
            rowRef.current?.updated_at ?? row?.updated_at ?? "";
          await api.patchStocktakeLine(
            stocktakeId,
            chainRootId,
            Math.round(value),
            knownUpdatedAt,
            restampCountedAt,
            countExpressionOverride,
          );
          // Refetch for the new updated_at; the line endpoint returns
          // only the line, not the parent.
          const fresh = await api.getStocktake(stocktakeId);
          rowRef.current = fresh;
          setRow(fresh);
          // Update local counts from the server response so any
          // server-side normalisation lands too.
          setCounts((prev) => {
            const next = new Map(prev.byRoot);
            const line = fresh.lines.find(
              (l) => l.article_chain_root_id === chainRootId,
            );
            next.set(chainRootId, line?.counted_qty ?? Math.round(value));
            return { byRoot: next };
          });
          if (restampCountedAt) {
            // Audit-fix MODERATE: optimistically bring
            // ``expected.byRoot[root]`` into alignment with the
            // ``expected.nowByRoot[root]`` value the operator just
            // confirmed. By definition of a restamp the two are
            // equal post-PATCH; without this, the local drift map
            // (and the visible ↻ button + "since: −N" line) lingers
            // until the next bootstrap refetch, inviting the
            // operator to double-tap and emit a duplicate
            // ``stocktake_line_recounted`` audit. Pull a fresh
            // bootstrap right after to also pick up any concurrent
            // sales/refills.
            setExpected((prev) => {
              if (!prev) return prev;
              const nowVal = prev.nowByRoot.get(chainRootId);
              if (nowVal === undefined) return prev;
              const nextByRoot = new Map(prev.byRoot);
              nextByRoot.set(chainRootId, nowVal);
              return { ...prev, byRoot: nextByRoot };
            });
            // Inlined inside the success path so a 412/422/offline
            // rollback skips this refresh entirely.
            void refreshBootstrap();
          }
        };
        try {
          await exec();
        } catch (err) {
          // Roll the optimistic local count back so the user sees the
          // last-known-server value, not their just-typed-but-not-saved
          // entry. Then surface the error via the modal / banner path.
          setCounts((prev) => {
            const nextMap = new Map(prev.byRoot);
            if (prevForRoot === undefined) {
              nextMap.delete(chainRootId);
            } else {
              nextMap.set(chainRootId, prevForRoot);
            }
            return { byRoot: nextMap };
          });
          if (handleMutationError(err, exec)) return;
          if (err instanceof ApiError) {
            // Round-2-pass-2 audit MAJOR: surface the structured
            // ``cascade_needs_buy_price`` 422 with a clear operator
            // message instead of dumping the JSON envelope. The
            // operator's fix is to add a buy_price for the chain root
            // — they go to the article-detail page from the row chip.
            if (err.errorCode === "cascade_needs_buy_price") {
              setError(
                "This edit would create a Stock-loss but the article " +
                  "has no buy_price. Add a buy_price effective on or " +
                  "before the line's counted_at, then retry.",
              );
              return;
            }
            setError(err.message);
          }
        }
      }),
    [stocktakeId, runMutation, handleMutationError, row, counts.byRoot, refreshBootstrap],
  );

  /** §5 (add-pos-recipes-and-cups): capture the measured contents of the OPEN
   * container for a volumetric article at count time, in base units (mL for
   * LITERS, g for GRAMS), ``null`` to clear. Reuses the same serialised
   * mutation queue + If-Match + refetch contract as ``commitCount``. The PATCH
   * is replace-semantics on the line, so we re-send the line's CURRENT
   * counted_qty alongside the new open level (never mutating the count). */
  const commitMeasuredOpen = useCallback(
    (chainRootId: number, baseUnits: number | null): Promise<void> =>
      runMutation(async () => {
        if (!Number.isFinite(stocktakeId)) return;
        if (baseUnits !== null && (!Number.isFinite(baseUnits) || baseUnits < 0))
          return;
        const exec = async () => {
          const knownUpdatedAt =
            rowRef.current?.updated_at ?? row?.updated_at ?? "";
          const line = (rowRef.current ?? row)?.lines.find(
            (l) => l.article_chain_root_id === chainRootId,
          );
          const countedQty =
            line?.counted_qty ?? counts.byRoot.get(chainRootId) ?? 0;
          await api.patchStocktakeLine(
            stocktakeId,
            chainRootId,
            countedQty,
            knownUpdatedAt,
            false,
            undefined,
            baseUnits === null ? null : Math.round(baseUnits),
          );
          const fresh = await api.getStocktake(stocktakeId);
          rowRef.current = fresh;
          setRow(fresh);
        };
        try {
          await exec();
        } catch (err) {
          if (handleMutationError(err, exec)) return;
          if (err instanceof ApiError) setError(err.message);
        }
      }),
    [stocktakeId, runMutation, handleMutationError, row, counts.byRoot],
  );

  /** "↻ re-walk" commit: bump ``counted_qty`` to ``value`` AND restamp
   * ``line.counted_at = NOW``. The bootstrap refresh + optimistic
   * ``expected.byRoot`` update are inlined inside ``commitCount``'s
   * success path (gated on ``restampCountedAt``), so a 412/422/offline
   * rollback skips them. This wrapper is a thin alias kept for call-
   * site readability — the drift+restamp UX surfaces use it. */
  const commitCountWithRestamp = useCallback(
    (chainRootId: number, value: number): Promise<void> =>
      commitCount(chainRootId, value, true),
    [commitCount],
  );

  /** Patch the stocktake header's ``counted_at`` field.
   *
   * Pre-finalize: a simple header edit.
   *
   * Post-finalize: the backend cascades ``effective_at`` onto every
   * linked ``stock_adjustment`` row in the same transaction so the
   * stock-state buckets stay aligned with the operator's intent.
   *
   * The mutation is serialised through ``runMutation`` so a rapid
   * counted_at edit + line PATCH burst doesn't 412 each other on the
   * shared ``updated_at`` token. */
  const commitCountedAt = useCallback(
    (next: dayjs.Dayjs | null): Promise<void> =>
      runMutation(async () => {
        if (!row) return;
        const iso = next ? next.toISOString() : null;
        const before = row.counted_at ?? null;
        if (iso === before) return; // no-op
        const exec = async () => {
          const fresh = await api.patchStocktake(
            row.id,
            { counted_at: iso },
            row.updated_at ?? "",
          );
          // Merge the patched header back over the local detail row so
          // ``updated_at`` (and the cascaded counted_at) stay in lock-step
          // with the server.
          setRow((prev) =>
            prev ? { ...prev, ...fresh } : prev,
          );
        };
        try {
          await exec();
        } catch (err) {
          if (handleMutationError(err, exec)) return;
          if (err instanceof ApiError) {
            // Round-2-pass-2 audit MODERATE: clearer copy for the
            // explicit refusal to clear counted_at — the backend's
            // structured 422 is otherwise just dumped JSON.
            if (err.errorCode === "clearing_counted_at_unsupported") {
              setError(
                "Counted-at cannot be cleared — enter a concrete date " +
                  "and time instead.",
              );
              return;
            }
            setError(err.message);
          }
        }
      }),
    [row, runMutation, handleMutationError],
  );

  /** Commit ``comment`` on blur. Same plumbing as commitCountedAt:
   * If-Match round-trip, mutation-queue serialised, merge the patched
   * header back over the local row. Pre-finalize the comment is a
   * free-form operator note ("morning count, two crates of beer
   * broken"); post-finalize it stays editable since the field is
   * informational and doesn't drive any cascade. The local TextArea
   * buffers typing — we only fire on blur so we don't burn an If-
   * Match per keystroke. */
  const commitComment = useCallback(
    (next: string | null): Promise<void> =>
      runMutation(async () => {
        if (!row) return;
        const before = row.comment ?? null;
        const normalised = next === null || next === "" ? null : next;
        if (normalised === before) return; // no-op — pure tab-through
        const exec = async () => {
          const fresh = await api.patchStocktake(
            row.id,
            { comment: normalised },
            row.updated_at ?? "",
          );
          setRow((prev) => (prev ? { ...prev, ...fresh } : prev));
        };
        try {
          await exec();
        } catch (err) {
          if (handleMutationError(err, exec)) return;
          if (err instanceof ApiError) setError(err.message);
        }
      }),
    [row, runMutation, handleMutationError],
  );

  // -------------------------------------------------------------------------
  // Numpad-debounce machinery (declared early so the scanner effect can
  // call flushPendingCommit on Enter — Enter is the operator's "I'm
  // done with this row" gesture, so we don't want the 300ms tail).
  // -------------------------------------------------------------------------
  const numpadCommitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const pendingCommitRef = useRef<{ root: number; value: number } | null>(null);

  const flushPendingCommit = useCallback(() => {
    if (numpadCommitTimerRef.current) {
      clearTimeout(numpadCommitTimerRef.current);
      numpadCommitTimerRef.current = null;
    }
    const pending = pendingCommitRef.current;
    pendingCommitRef.current = null;
    if (pending) {
      void commitCount(pending.root, pending.value);
    }
  }, [commitCount]);

  // -------------------------------------------------------------------------
  // Scanner: USB-wedge keydown listener
  // -------------------------------------------------------------------------

  /** Buffer + last-key-time live in refs so the handler reads fresh
   * values without re-binding on every change. */
  const scanBufferRef = useRef("");
  const scanLastAtRef = useRef(0);
  const scanFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fireScan = useCallback(
    async (barcode: string): Promise<void> => {
      if (!Number.isFinite(stocktakeId) || !barcode) return;
      const key = newUuid();
      const exec = async () => {
        let res: ScanResponse;
        try {
          res = await api.scanStocktake(stocktakeId, {
            barcode,
            qty: 1,
            idempotency_key: key,
          });
        } catch (err) {
          if (handleMutationError(err, exec)) return;
          if (err instanceof ApiError) setError(err.message);
          return;
        }
        if (!res.resolved) {
          // Enqueue (don't replace) so a rapid burst of unresolved
          // scans is preserved for the operator to triage one by one.
          // De-dupe on the way in: an idle USB-wedge that double-fires
          // the same code shouldn't show up twice.
          setUnknownBarcode((prev) =>
            prev.includes(res.barcode) ? prev : [...prev, res.barcode],
          );
          return;
        }
        // Resolved → bump local count + reflect in row.
        setCounts((prev) => {
          const next = new Map(prev.byRoot);
          next.set(res.chain_root_id, res.counted_qty);
          return { byRoot: next };
        });
        setActiveRoot(res.chain_root_id);
        setNumpadValue(String(res.counted_qty));
        // Best-effort row refresh so updated_at + lines list stay in
        // sync with the server.
        try {
          const fresh = await api.getStocktake(stocktakeId);
          setRow(fresh);
        } catch {
          // Non-fatal; the next mutation will retry.
        }
        if (feedbackEnabled) {
          playBeep();
          vibrate();
        }
      };
      await runMutation(exec);
    },
    [stocktakeId, runMutation, handleMutationError, feedbackEnabled],
  );

  useEffect(() => {
    // Pause the scanner while the finalize-preview modal is up.
    // A stray USB-wedge scan during the preview would mutate the
    // counted_qty the operator is currently reviewing, silently
    // making the preview values stale.
    if (!scannerEnabled || editingDisabled || isFinalized || preview !== null)
      return;
    const flush = () => {
      const code = scanBufferRef.current.trim();
      scanBufferRef.current = "";
      if (code.length >= 4) {
        void fireScan(code);
      }
    };

    const onKey = (e: KeyboardEvent) => {
      // Per §8.3 / §8.8: filter out synthetic keystrokes from the
      // in-page numpad clicks. ``isTrusted`` is true only for native
      // user input (real keyboard / USB-wedge / actual touch),
      // never for the synthetic events we dispatch from <button>
      // clicks.
      if (!e.isTrusted) return;
      // Ignore modifier keys + when the focus is inside our numpad
      // input or any input where typing means "edit a field" rather
      // than "scan a barcode".
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName?.toLowerCase();
        if (tag === "input" || tag === "textarea" || target.isContentEditable) {
          return;
        }
      }
      const now = Date.now();
      // If more than 250ms since the last keystroke, treat this as a
      // brand-new scan (i.e. drop whatever stragglers were in the
      // buffer).
      if (now - scanLastAtRef.current > 250) {
        scanBufferRef.current = "";
      }
      scanLastAtRef.current = now;

      if (e.key === "Enter") {
        e.preventDefault();
        if (scanFlushTimerRef.current) {
          clearTimeout(scanFlushTimerRef.current);
          scanFlushTimerRef.current = null;
        }
        // Enter also flushes any pending numpad-debounced commit so
        // the operator's "I'm done typing this row" gesture takes
        // effect immediately rather than waiting 300ms.
        flushPendingCommit();
        flush();
        return;
      }
      // Only collect printable single chars.
      if (e.key.length === 1) {
        scanBufferRef.current += e.key;
      }
      // 250ms idle timer flushes the buffer if Enter never lands.
      if (scanFlushTimerRef.current) {
        clearTimeout(scanFlushTimerRef.current);
      }
      scanFlushTimerRef.current = setTimeout(flush, 250);
    };

    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      if (scanFlushTimerRef.current) {
        clearTimeout(scanFlushTimerRef.current);
        scanFlushTimerRef.current = null;
      }
      // Reset partial-scan state on teardown — without this, toggling
      // the scanner off mid-burst would leave buffered digits in
      // place. When the operator flipped it back on, the next real
      // keystroke would either be glued onto stale chars (giving us
      // a corrupted barcode) or treated as continuation of a "scan"
      // that started before the toggle.
      scanBufferRef.current = "";
      scanLastAtRef.current = 0;
    };
  }, [scannerEnabled, editingDisabled, isFinalized, fireScan, preview, flushPendingCommit]);

  // -------------------------------------------------------------------------
  // Camera scanner (Chrome Android — BarcodeDetector)
  // -------------------------------------------------------------------------
  //
  // Injects the shake keyframe once on mount (id-guarded so HMR re-mounts
  // don't double-insert it). The frame name is namespaced so it can't
  // collide with anything else on the page.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.getElementById("stocktake-shake-keyframes")) return;
    const styleEl = document.createElement("style");
    styleEl.id = "stocktake-shake-keyframes";
    styleEl.appendChild(
      document.createTextNode(
        "@keyframes stockTakeShake {" +
          "0%,100%{transform:translateX(0);}" +
          "20%{transform:translateX(-6px);}" +
          "40%{transform:translateX(6px);}" +
          "60%{transform:translateX(-4px);}" +
          "80%{transform:translateX(4px);}" +
          "}" +
          // Flash animation for the "↻ re-walk" button when it first
          // appears (or when the active row changes to one that has
          // drift). Brief, scoped to .restamp-flash — see Mobile +
          // Desktop ↻ button renders.
          "@keyframes stockTakeRestampFlash {" +
            "0%{background-color:rgba(255,229,143,0.8);}" +
            "100%{background-color:transparent;}" +
          "}" +
          ".restamp-flash{animation:stockTakeRestampFlash 600ms ease-out 1;}",
      ),
    );
    document.head.appendChild(styleEl);
  }, []);

  // Drive the camera lifecycle. Opens the rear camera, kicks off the
  // detection loop at ~5Hz, and tears everything down on close /
  // unmount. Errors (permission denied, no camera) surface via setError
  // and close the modal.
  useEffect(() => {
    if (!cameraOpen) return;
    // ``BarcodeDetector`` and ``navigator.mediaDevices`` both require a
    // secure context (HTTPS or ``localhost``). Surfacing this as a clear
    // error beats silently failing — operators testing over plain HTTP
    // would otherwise tap the camera button and see nothing happen.
    if (typeof window !== "undefined" && !window.isSecureContext) {
      setError(
        "Camera scanning requires HTTPS. Switch to https:// or localhost to use the in-page scanner — USB-wedge scanning still works over HTTP.",
      );
      setCameraOpen(false);
      return;
    }
    if (!cameraSupported) {
      setError(
        "This browser doesn't support BarcodeDetector. Use Chrome on Android (≥83) over HTTPS — or scan with a USB-wedge keyboard scanner.",
      );
      setCameraOpen(false);
      return;
    }
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let detector: BarcodeDetectorLike | null = null;
    // Track the actual video element bound to the running stream so the
    // cleanup releases it even if videoRef.current later points to a
    // freshly-mounted node (e.g. after destroyOnClose).
    let boundVideo: HTMLVideoElement | null = null;
    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          boundVideo = video;
          video.srcObject = stream;
          await video.play().catch(() => undefined);
        }
        const Ctor = window.BarcodeDetector;
        if (!Ctor) return;
        detector = new Ctor();
        intervalId = setInterval(async () => {
          if (cancelled) return;
          const v = videoRef.current;
          if (!v || v.readyState < 2 || !detector) return;
          try {
            const codes = await detector.detect(v);
            if (cancelled) return;
            if (codes.length > 0) {
              const code = codes[0].rawValue;
              if (code) {
                setScanFlash("ok");
                window.setTimeout(() => setScanFlash(null), 700);
                void fireScan(code);
                setCameraOpen(false);
              }
            }
          } catch {
            // Best-effort — detect() throws on intermittent frames.
          }
        }, 200);
      } catch {
        if (cancelled) return;
        setError("Camera unavailable — check browser permissions.");
        setCameraOpen(false);
      }
    };
    void start();
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      const stream = streamRef.current;
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
      streamRef.current = null;
      if (boundVideo) {
        boundVideo.srcObject = null;
      }
    };
  }, [cameraOpen, cameraSupported, fireScan]);

  // Red flash + shake when an unknown barcode lands while the camera
  // modal is open. The UnknownBarcodeModal takes over from there; this
  // is just a visual cue that the scan was registered but unresolved.
  useEffect(() => {
    if (!cameraOpen || unknownBarcode.length === 0) return;
    setScanFlash("unknown");
    const t = window.setTimeout(() => setScanFlash(null), 700);
    return () => window.clearTimeout(t);
  }, [unknownBarcode.length, cameraOpen]);

  // -------------------------------------------------------------------------
  // Numpad: edit-by-tap (replace semantics)
  //
  // Per-keystroke commits are debounced 300ms (see flushPendingCommit
  // above). The optimistic local count still updates per key so the
  // row reflects the value immediately. The mutation chain still
  // serialises across debounced fires (and across rows).
  // -------------------------------------------------------------------------

  const onNumpadChange = useCallback(
    (next: string) => {
      setNumpadValue(next);
      if (activeRoot === null) return;
      const parsed = next === "" ? 0 : Number(next);
      if (!Number.isFinite(parsed) || parsed < 0) return;
      // Optimistic local update — snappy UI per keystroke.
      setCounts((prev) => {
        const m = new Map(prev.byRoot);
        m.set(activeRoot, Math.round(parsed));
        return { byRoot: m };
      });
      // Debounce the network commit. The mutation queue still
      // serialises if the operator types fast enough to fire multiple
      // debounced commits in flight.
      pendingCommitRef.current = { root: activeRoot, value: parsed };
      if (numpadCommitTimerRef.current) {
        clearTimeout(numpadCommitTimerRef.current);
      }
      numpadCommitTimerRef.current = setTimeout(() => {
        numpadCommitTimerRef.current = null;
        const pending = pendingCommitRef.current;
        pendingCommitRef.current = null;
        if (pending) void commitCount(pending.root, pending.value);
      }, 300);
    },
    [activeRoot, commitCount],
  );

  /** Clear (C) — fully UNCOUNT the active row, restoring it to the
   * "uncounted" state rather than committing a literal 0. Deletes the
   * stocktake line on the server (pre-finalize only; the C button is
   * gated by ``editingDisabled`` so it's never reachable post-finalize).
   * Mirrors ``commitCount``'s optimistic + If-Match + rollback contract. */
  /** Uncount a specific row (used by the desktop × button next to
   * ``RowCountInput``). Deletes the line on the server + drops the
   * local count optimistically. No-op when the line is already
   * uncounted. Mirrors ``uncountActive``'s optimistic + If-Match +
   * rollback contract; the only difference is the source of the
   * root (parameter vs. ``activeRoot`` state). */
  const uncountRoot = useCallback(
    (root: number): void => {
      if (root === activeRoot) {
        // Cancel any pending debounced commit so it can't re-create
        // the line at 0 right after we delete it.
        if (numpadCommitTimerRef.current) {
          clearTimeout(numpadCommitTimerRef.current);
          numpadCommitTimerRef.current = null;
        }
        pendingCommitRef.current = null;
        setNumpadValue("");
      }
      const prevForRoot = counts.byRoot.get(root);
      if (prevForRoot === undefined) return;
      setCounts((prev) => {
        const m = new Map(prev.byRoot);
        m.delete(root);
        return { byRoot: m };
      });
      void runMutation(async () => {
        const exec = async () => {
          // Audit-fix MINOR #8: see commitCount.exec.
          const knownUpdatedAt =
            rowRef.current?.updated_at ?? row?.updated_at ?? "";
          await api.deleteStocktakeLine(stocktakeId, root, knownUpdatedAt);
          const fresh = await api.getStocktake(stocktakeId);
          rowRef.current = fresh;
          setRow(fresh);
        };
        try {
          await exec();
        } catch (err) {
          setCounts((prev) => {
            const m = new Map(prev.byRoot);
            m.set(root, prevForRoot);
            return { byRoot: m };
          });
          if (handleMutationError(err, exec)) return;
          if (err instanceof ApiError) setError(err.message);
        }
      });
    },
    [
      activeRoot,
      counts.byRoot,
      stocktakeId,
      row,
      runMutation,
      handleMutationError,
    ],
  );

  const uncountActive = useCallback((): void => {
    if (activeRoot === null) return;
    uncountRoot(activeRoot);
  }, [activeRoot, uncountRoot]);

  /** 0044: post-finalize revert. The backend ``DELETE /lines/{root}``
   * route handles BOTH pre-finalize hard-delete (called via
   * ``uncountRoot`` above) AND post-finalize soft-revert (called here)
   * — the difference is wholly server-side, but the UI is gated on
   * ``isFinalized`` and pops a confirm modal so the operator
   * understands the heavier semantic. */
  const revertLine = useCallback(
    (chainRootId: number): void => {
      if (!row) return;
      const line = row.lines.find(
        (l) => l.article_chain_root_id === chainRootId,
      );
      if (!line) return;
      const lossCents =
        (line.buy_price_cents_at_finalize ?? 0)
        * Math.min(
          0,
          (line.counted_qty ?? 0)
            - (line.expected_qty_at_finalize ?? 0),
        );
      const lossEuro = (Math.abs(lossCents) / 100).toFixed(2);
      const delta =
        (line.counted_qty ?? 0) - (line.expected_qty_at_finalize ?? 0);
      // Verify-fix MODERATE #7: only promise to delete the
      // Stock-loss expense when one is actually LIVE. If the
      // operator already soft-deleted it, the backend leaves the
      // ghost row alone (sticky opt-out) — surfacing "delete the
      // Stock-loss expense" anyway would be misleading.
      const lossState = line.loss_expense_state ?? "none";
      const willDeleteLoss = lossState === "live" && lossCents !== 0;
      Modal.confirm({
        title: "Revert this article's count?",
        content: (
          <div>
            <p>This will:</p>
            <ul style={{ margin: "4px 0 4px 18px", paddingLeft: 0 }}>
              {delta !== 0 && (
                <li>
                  delete the linked stock adjustment (Δ&nbsp;
                  {delta > 0 ? `+${delta}` : delta})
                </li>
              )}
              {willDeleteLoss && (
                <li>
                  delete the Stock-loss expense (€{lossEuro})
                </li>
              )}
              {lossState === "soft_deleted" && (
                <li>
                  leave the (already-soft-deleted) Stock-loss expense
                  untouched
                </li>
              )}
              {delta === 0 && !willDeleteLoss && lossState !== "soft_deleted" && (
                <li>this line had no economic impact already</li>
              )}
            </ul>
            <p style={{ margin: 0 }}>
              The line stays as a ghost row for audit. You can start a
              new stocktake covering only this article to re-measure.
            </p>
          </div>
        ),
        okText: "Revert",
        okType: "danger",
        cancelText: "Cancel",
        // Verify-fix MAJOR #3: return the promise from onOk so AntD
        // keeps the modal open + shows its spinner until the DELETE
        // + refetch completes. Throwing inside keeps it open on
        // error so the operator sees the inline result.
        onOk: () =>
          runMutation(async () => {
            const knownUpdatedAt =
              rowRef.current?.updated_at ?? row.updated_at ?? "";
            try {
              await api.deleteStocktakeLine(
                row.id,
                chainRootId,
                knownUpdatedAt,
              );
              const fresh = await api.getStocktake(row.id);
              rowRef.current = fresh;
              setRow(fresh);
              // Verify-fix MAJOR #4: refresh counts.byRoot from the
              // server's response so the row's "Counted" column +
              // mismatch chip update alongside the new "reverted"
              // badge. Without this, post-revert the row would
              // simultaneously show the pre-revert count + the
              // ghost-row badge.
              setCounts((prev) => {
                const next = new Map(prev.byRoot);
                const line = fresh.lines.find(
                  (l) => l.article_chain_root_id === chainRootId,
                );
                if (line) {
                  next.set(chainRootId, line.counted_qty);
                }
                return { byRoot: next };
              });
            } catch (err) {
              if (
                err instanceof ApiError
                && err.errorCode === "missing_finalize_snapshot"
              ) {
                setError(
                  "This line was finalized before the per-line "
                  + "snapshot rework and can't be auto-reverted.",
                );
                throw err;
              }
              if (
                err instanceof ApiError
                && err.errorCode === "line_already_reverted"
              ) {
                // Idempotent server case — silently close.
                return;
              }
              if (err instanceof ApiError) {
                setError(err.message);
              }
              throw err;
            }
          }),
      });
    },
    [row, runMutation, handleMutationError],
  );

  const selectRow = useCallback(
    (root: number) => {
      // Switching rows must flush any pending edit against the OLD
      // active row first — otherwise the debounced commit would fire
      // against the now-current row with the OLD value, or worse,
      // race the new row's first keystroke.
      flushPendingCommit();
      // Tap = full selection: bump both activeRoot (info bar) and
      // selectedRoot (list highlight). Scans only bump activeRoot so
      // the list highlight stays anchored on the operator's last tap.
      setActiveRoot(root);
      setSelectedRoot(root);
      // Tapping a row is the operator's "I want to count this" gesture
      // — pop the keypad back up if it was collapsed.
      setKeypadCollapsed(false);
      const v = counts.byRoot.get(root);
      setNumpadValue(v === undefined ? "" : String(v));
    },
    [counts, flushPendingCommit],
  );

  // 0051: chip-strip commit path. Receives (terms, countedQty) from the
  // CountExpressionInline component and PATCHes both atomically through
  // the existing commitCount mutation queue. Local counts.byRoot is
  // updated optimistically so the mismatch chip re-renders immediately.
  const onTermsEdit = useCallback(
    (
      root: number,
      terms: import("../../utils/countExpression").Term[],
      countedQty: number,
    ) => {
      setCounts((prev) => {
        const m = new Map(prev.byRoot);
        m.set(root, Math.round(countedQty));
        return { byRoot: m };
      });
      void commitCount(root, countedQty, false, terms);
    },
    [commitCount],
  );

  // -------------------------------------------------------------------------
  // Mismatch chip
  // -------------------------------------------------------------------------

  /** Returns null when the expected snapshot is missing OR when the
   * row has never been counted (no entry in ``counts.byRoot``). The
   * latter is important: §8.3 "uncounted" rows should NOT render a
   * mismatch chip — otherwise every untouched article would show a
   * misleading "-N" chip purely from the missing zero. */
  const computeMismatch = useCallback(
    (root: number): number | null => {
      const exp = expected?.byRoot.get(root);
      if (exp === undefined) return null;
      const counted = counts.byRoot.get(root);
      if (counted === undefined) return null;
      return counted - exp;
    },
    [expected, counts],
  );

  const renderMismatchChip = (root: number) => {
    const counted = counts.byRoot.get(root);
    if (counted === undefined) {
      return <Tag color="default">uncounted</Tag>;
    }
    const m = computeMismatch(root);
    if (m === null) return <Text type="secondary">—</Text>;
    if (m === 0) return <Tag color="green">∆ 0</Tag>;
    if (m < 0) return <Tag color="red">∆ {m}</Tag>;
    return <Tag color="orange">∆ +{m}</Tag>;
  };

  // -------------------------------------------------------------------------
  // Finalize flow
  // -------------------------------------------------------------------------

  const openPreview = async () => {
    setPreviewBusy(true);
    try {
      const p = await api.previewFinalizeStocktake(stocktakeId);
      setPreview(p);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    } finally {
      setPreviewBusy(false);
    }
  };

  /** §8.6: re-fetch the finalize preview after the operator clears a
   * blocker inline (e.g. setting buy_price). Called by
   * FinalizePreviewBody so the blocker drops off the alert as soon as
   * the cascade succeeds. */
  const refreshPreview = useCallback(async () => {
    try {
      const p = await api.previewFinalizeStocktake(stocktakeId);
      setPreview(p);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    }
  }, [stocktakeId]);

  const confirmFinalize = async () => {
    setPreviewBusy(true);
    try {
      await api.finalizeStocktake(stocktakeId);
      setPreview(null);
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    } finally {
      setPreviewBusy(false);
    }
  };

  // -------------------------------------------------------------------------
  // Delete flow
  // -------------------------------------------------------------------------

  const confirmDelete = async () => {
    if (!row) return;
    if (String(row.id) !== deleteText.trim()) {
      setError(`Type "${row.id}" to confirm deletion.`);
      return;
    }
    // An empty If-Match header is a perfectly valid header that just
    // won't match anything → backend 412. Without the
    // handleMutationError branching used by the line PATCHes, a 412
    // here would silently set a generic error message instead of
    // surfacing the stale banner with a Reload affordance. Wire it
    // up the same way so missing updated_at + concurrent edits both
    // funnel through the same "reload + retry" path.
    const exec = async () => {
      await api.deleteStocktake(row.id, row.updated_at ?? "");
      navigate("/stock/stocktakes");
    };
    try {
      await exec();
    } catch (err) {
      if (handleMutationError(err, exec)) return;
      if (err instanceof ApiError) setError(err.message);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (!row && loading) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }
  if (!row) {
    return (
      <Card>
        <Alert
          type="error"
          message={error ?? "Stocktake not found."}
          showIcon
        />
        <div style={{ marginTop: 16 }}>
          <Button onClick={() => navigate("/stock/stocktakes")}>← Back</Button>
        </div>
      </Card>
    );
  }

  // Build the table data for desktop + the card list for mobile.
  //
  // ``counted`` is intentionally ``null`` when the row hasn't been
  // touched yet (no stocktake_line on the server). The UI distinguishes
  // "untouched" (empty input, no mismatch chip) from "counted as 0"
  // (explicit zero with mismatch chip rendered).
  const buildRows = (list: ArticleRow[]) =>
    list
      .map((a) => {
        const raw = counts.byRoot.get(a.chain_root_id);
        const counted: number | null = raw === undefined ? null : raw;
        const exp = expected?.byRoot.get(a.chain_root_id) ?? null;
        // List highlight tracks the LAST-TAPPED row only (selectedRoot),
        // not the scanned active row. This keeps the operator's visual
        // anchor on the no-barcode row they were counting even when a
        // wedge scan bumps the info bar.
        const isActive = a.chain_root_id === selectedRoot;
        return {
          article: a,
          root: a.chain_root_id,
          name: a.name,
          counted,
          expected: exp,
          active: isActive,
          revertedAt: revertedAtByRoot.get(a.chain_root_id) ?? null,
        };
      })
      // "Hide counted" filter — drop rows that already have a count so
      // the operator's worklist shrinks as they progress. The activeRoot
      // exception keeps the currently-counting row visible even after
      // its count is committed (otherwise it would vanish mid-edit).
      // Post-finalize the filter is bypassed entirely: every counted row
      // has a count (counts.byRoot is seeded from st.lines on refresh),
      // so leaving the filter on would collapse the list to nothing and
      // hide the edit-cascade entry points.
      .filter((r) => {
        if (isFinalized) return true;
        if (!hideCounted) return true;
        if (r.counted === null) return true;
        if (r.root === activeRoot) return true;
        // Keep the row whose chip-strip input is focused visible, so
        // committing a term with Enter doesn't hide it until the operator
        // leaves the field.
        if (r.root === editingRoot) return true;
        return false;
      });

  const noBarcodeRows = buildRows(noBarcode);
  const withBarcodeRows = buildRows(withBarcode);

  // ------ Article row table (desktop) ------
  const rowColumns: ColumnsType<ReturnType<typeof buildRows>[number]> = [
    {
      title: "Article",
      dataIndex: "name",
      render: (name: string, r) => (
        <Space size={6}>
          <Text strong={r.active}>{name}</Text>
          {r.article.barcodes.length === 0 && (
            <Tooltip title="No barcode attached">
              <Tag color="default">no-barcode</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Expected",
      dataIndex: "expected",
      width: 100,
      align: "right",
      render: (v: number | null) =>
        v === null ? <Text type="secondary">—</Text> : v,
    },
    {
      title: "Counted",
      dataIndex: "counted",
      // Wide: the chip strip (pills + typed-prefix input + running total)
      // needs room so a multi-term expression doesn't wrap.
      width: 380,
      render: (_v: number | null, r) => (
        // 0051: chip strip — green/red pill per term, a typed-prefix input
        // for new commits, and a `= total` running total. Pre-finalize the
        // chip strip is the primary editor; legacy lines surface as a
        // single sealed loose chip until the operator commits a fresh term.
        // §8.7 post-finalize: the cascade splits Stock-loss + Stock-broken
        // from the server-side count_expression atomically with the PATCH.
        // Shares the same commit queue + If-Match concurrency via commitCount.
        <CountExpressionInline
          terms={countExpressionByRoot.get(r.root) ?? null}
          packSize={r.article.pack_size ?? null}
          disabled={editingDisabled || r.revertedAt !== null}
          onCommit={(terms, countedQty) =>
            onTermsEdit(r.root, terms, countedQty)
          }
          onFocusChange={(focused) =>
            setEditingRoot(focused ? r.root : (cur) => (cur === r.root ? null : cur))
          }
        />
      ),
    },
    {
      title: "Mismatch",
      width: 110,
      render: (_: unknown, r) => renderMismatchChip(r.root),
    },
    {
      // Verify-fix MAJOR #5: flat-Table view was missing the revert
      // affordance entirely — operators with Tree View OFF couldn't
      // revert a line or see which ones were reverted.
      title: "Actions",
      width: 100,
      render: (_: unknown, r) => {
        if (r.revertedAt !== null) {
          return (
            <Tag
              color="default"
              title={`Reverted ${formatCompactDateTime(r.revertedAt)}`}
              style={{ margin: 0, fontSize: 11 }}
            >
              reverted
            </Tag>
          );
        }
        if (isFinalized && r.counted !== null) {
          return (
            <Button
              size="small"
              danger
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => revertLine(r.root)}
              aria-label="Revert this article's count"
              title="Revert: undo the linked stock adjustment + Stock-loss expense"
              style={{ padding: "0 6px", fontSize: 14, fontWeight: 500 }}
            >
              ↶
            </Button>
          );
        }
        return null;
      },
    },
  ];

  // ------ Mobile-style card list ------
  //
  // Two render modes:
  //   - flat: list rendered in document order (A-Z from buildRows)
  //   - tree: grouped by category → product group via metaByRoot
  //
  // When treeView is on we use this same renderer on desktop too — the
  // card layout is denser than an antd Table with collapsed grouping and
  // keeps the per-row click semantics identical across breakpoints.
  const groupRows = (rows: ReturnType<typeof buildRows>) => {
    const tree = new Map<string, Map<string, typeof rows>>();
    for (const r of rows) {
      const meta = metaByRoot.get(r.root) ?? { category: "Uncategorised", group: "Ungrouped" };
      if (!tree.has(meta.category)) tree.set(meta.category, new Map());
      const g = tree.get(meta.category)!;
      if (!g.has(meta.group)) g.set(meta.group, []);
      g.get(meta.group)!.push(r);
    }
    // Sort categories A-Z, but pin the "Uncategorised" bucket to the
    // bottom so the named taxonomy lands at the top of the screen.
    return Array.from(tree.entries())
      .sort(([a], [b]) => a === "Uncategorised" ? 1 : b === "Uncategorised" ? -1 : a.localeCompare(b))
      .map(([category, groupMap]) => ({
        category,
        groups: Array.from(groupMap.entries())
          .sort(([a], [b]) => a === "Ungrouped" ? 1 : b === "Ungrouped" ? -1 : a.localeCompare(b))
          .map(([group, items]) => ({ group, items })),
      }));
  };

  const renderMobileList = (
    list: ReturnType<typeof buildRows>,
    label: string,
  ) => {
    if (list.length === 0) return null;
    const renderSingleRow = (r: ReturnType<typeof buildRows>[number]) => (
      <div
        key={r.root}
        onClick={() => selectRow(r.root)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: isMobile ? "2px 8px" : "6px 10px",
          marginTop: isMobile ? 1 : 4,
          borderRadius: 4,
          cursor: "pointer",
          background: r.revertedAt !== null
            ? "rgba(0,0,0,0.04)"
            : r.active
              ? "rgba(24,144,255,0.12)"
              : "rgba(0,0,0,0.02)",
          border: r.active
            ? "1px solid #1890ff"
            : "1px solid rgba(0,0,0,0.06)",
          opacity: r.revertedAt !== null ? 0.55 : 1,
        }}
      >
        <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          <Text
            strong={r.active}
            style={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              // Mobile gets a bigger bump (16) for tap readability;
              // desktop stays at 15 because the proper Exp/Cnt columns
              // already increase density.
              fontSize: isMobile ? 16 : 15,
              lineHeight: 1.25,
            }}
          >
            {r.name}
          </Text>
        </div>
        {/* 0044: ↶ revert action mirrored on mobile + desktop for
         * post-finalize stocktakes. Per user spec, the only
         * post-finalize affordance the operator needs on mobile is
         * the revert button at the top — the rest (Exp/Cnt/×/↻)
         * stays desktop-only because mobile uses the keypad for
         * counting. */}
        {isFinalized
          && r.counted !== null
          && r.revertedAt === null && (
          <div onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
            <Button
              // Verify-fix MINOR #9: drop ``size="small"`` on mobile
              // so the rendered button hits the WCAG 2.5.5 44×44
              // minimum tap target (matches the keypad's own
              // ≥44px policy in Numpad.tsx).
              size={isMobile ? "middle" : "small"}
              danger
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => revertLine(r.root)}
              aria-label="Revert this article's count"
              title="Revert: undo the linked stock adjustment + Stock-loss expense"
              style={{
                padding: "0 6px",
                fontSize: isMobile ? 18 : 14,
                fontWeight: 500,
                ...(isMobile
                  ? { minWidth: 44, minHeight: 44 }
                  : {}),
              }}
            >
              ↶
            </Button>
          </div>
        )}
        {r.revertedAt !== null && (
          <Tag
            color="default"
            title={`Reverted ${formatCompactDateTime(r.revertedAt)}`}
            style={{ margin: 0, fontSize: 11, flexShrink: 0 }}
          >
            reverted
          </Tag>
        )}
        {/* Desktop card layout: surface the inline number input
         * because the Numpad is mobile-only. Gated by !isMobile so
         * on-screen keyboards don't pop up when tapping a row on
         * mobile (the numpad is the only intended edit affordance
         * there). The stopPropagation wrapper prevents taps inside
         * the input from also firing the outer card's selectRow. */}
        {!isMobile && (() => {
          const expectedNow = expected?.nowByRoot.get(r.root) ?? null;
          const driftSinceCount =
            r.expected !== null
            && expectedNow !== null
            && r.counted !== null
              ? expectedNow - r.expected
              : null;
          const restampValue = driftSinceCount !== null && driftSinceCount !== 0
            ? Math.max(0, expectedNow ?? 0)
            : null;
          return (
            <>
              <div style={{ width: 60, textAlign: "right", flexShrink: 0 }}>
                <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Exp</Text>
                <Text style={{ fontSize: 14 }}>{r.expected ?? "—"}</Text>
              </div>
              <div
                onClick={(e) => e.stopPropagation()}
                // Wide, growing calc column (the calc chain is the focus of
                // this screen); the name column yields the space. minWidth
                // keeps a multi-term expression from wrapping.
                style={{ flex: 3, minWidth: 320 }}
              >
                {/* 0051: the desktop tree/card view uses the same chip-strip
                 * calc chain as the flat-Table view, so the calculation
                 * (green/red pills + typed-prefix input + running total) is
                 * visible in EVERY desktop layout, not only Tree-off. */}
                <CountExpressionInline
                  terms={countExpressionByRoot.get(r.root) ?? null}
                  packSize={r.article.pack_size ?? null}
                  // 0044: reverted rows are economically inactive ghost
                  // rows; the backend would 422 ``line_already_reverted``.
                  disabled={editingDisabled || r.revertedAt !== null}
                  onCommit={(terms, countedQty) =>
                    onTermsEdit(r.root, terms, countedQty)
                  }
                  onFocusChange={(focused) =>
                    setEditingRoot(focused ? r.root : (cur) => (cur === r.root ? null : cur))
                  }
                />
              </div>
              {/* Desktop action icons:
               * - ↻ (pre-finalize): re-walk + restamp (drift only)
               * - × (pre-finalize): uncount (counted rows)
               * - ↶ (post-finalize): revert (counted non-reverted rows)
               * 0044: reverted rows show NO action icons — they're
               * already economically inactive ghost rows. */}
              <div
                onClick={(e) => e.stopPropagation()}
                style={{ display: "flex", gap: 4, flexShrink: 0, width: 80 }}
              >
                {!isFinalized && restampValue !== null && (
                  <Button
                    key={`desktop-restamp-${r.root}`}
                    size="small"
                    disabled={editingDisabled}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => void commitCountWithRestamp(r.root, restampValue)}
                    className="restamp-flash"
                    aria-label={`Re-walked, set counted to ${restampValue}`}
                    title={`Re-walk: counted := ${restampValue}, anchor := NOW`}
                    style={{ padding: "0 6px", fontSize: 13 }}
                  >
                    ↻
                  </Button>
                )}
                {!isFinalized && r.counted !== null && (
                  <Button
                    size="small"
                    danger
                    disabled={editingDisabled}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => uncountRoot(r.root)}
                    aria-label="Uncount this row"
                    title="Uncount this row"
                    style={{ padding: "0 6px", fontSize: 14, fontWeight: 500 }}
                  >
                    ×
                  </Button>
                )}
              </div>
            </>
          );
        })()}
        <div style={{ flexShrink: 0 }}>
          {renderMismatchChip(r.root)}
        </div>
      </div>
    );

    if (treeView) {
      const grouped = groupRows(list);
      return (
        <div style={{ marginBottom: 12 }}>
          {label !== "" && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {label}
            </Text>
          )}
          {grouped.map(({ category, groups: groupList }) => (
            <div key={category} style={{ marginTop: 3 }}>
              <Text type="secondary" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, lineHeight: 1.2 }}>
                {category}
              </Text>
              {groupList.map(({ group, items }) => (
                <div key={group} style={{ marginTop: 1 }}>
                  <Text type="secondary" style={{ fontSize: 10, marginLeft: 8, lineHeight: 1.2 }}>{group}</Text>
                  {items.map((r) => renderSingleRow(r))}
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    }

    return (
      <div style={{ marginBottom: 12 }}>
        {label !== "" && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {label}
          </Text>
        )}
        {list.map((r) => renderSingleRow(r))}
      </div>
    );
  };

  return (
    <Card
      styles={{ body: {
        // Reserve room under the list so the fixed-bottom mobile
        // keypad doesn't overlay article rows. Collapsed → info view
        // + a 3-button right column (collapse/search/camera ≈ 160px);
        // expanded → info view + "=" + numpad stacked (~360px).
        paddingBottom: isMobile
          ? keypadCollapsed
            ? 170
            : 360
          : undefined,
      } }}
    >
      {/* ------------- Sticky header (id, counted_at, status, save) -------------
       *
       * Theme-aware: no hard-coded ``background: white`` (rendered the
       * whole header as a white bar in dark mode, hiding the Title
       * text + Switch labels). The Title carries NO colour override so
       * AntD's theme tokens drive the foreground. The toolbar row
       * stacks via the ``isMobile`` breakpoint instead of relying on
       * implicit flex-wrap so labels and pickers don't crowd the
       * search input on narrow screens. */}
      <div
        style={{
          ...(isMobile ? {} : { position: "sticky", top: 0, zIndex: 10 }),
          background: "var(--ant-color-bg-container, transparent)",
          paddingBottom: 8,
          marginBottom: 12,
          borderBottom:
            "1px solid var(--ant-color-border-secondary, rgba(0,0,0,0.06))",
        }}
      >
        <Row align="middle" gutter={[16, 8]} wrap>
          <Col xs={24} sm={12}>
            <Space wrap align="center">
              <Button onClick={() => navigate("/stock/stocktakes")}>← Back</Button>
              {/* No colour override — AntD picks the right foreground
               * for light + dark themes via its design tokens. */}
              <Title level={3} style={{ margin: 0 }}>
                Stocktake #{row.id}
              </Title>
              {isFinalized ? (
                <Tag color="green">finalized</Tag>
              ) : (
                <Tag color="orange">in-progress</Tag>
              )}
            </Space>
          </Col>
          <Col
            xs={24}
            sm={12}
            style={{ textAlign: isMobile ? "left" : "right" }}
          >
            <Space wrap>
              <Text type="secondary">Counted at:</Text>
              {/* Post-finalize edits cascade ``effective_at`` onto every
               * linked stock_adjustment row server-side. The tooltip
               * tells the operator that BEFORE they edit, not after. */}
              <Tooltip
                title={
                  isFinalized
                    ? "Editing counted_at cascades the stock_adjustment effective_at on this finalized stocktake."
                    : "Pre-finalize edit; finalize will snapshot this as the as-of time."
                }
              >
                <DatePicker
                  showTime
                  format="DD.MM.YYYY HH:mm"
                  value={row.counted_at ? dayjs(row.counted_at) : null}
                  onChange={(next) => void commitCountedAt(next)}
                  disabled={editingDisabled}
                />
              </Tooltip>
            </Space>
          </Col>
        </Row>

        {/* Operator note. Surfaced under the back/title row so it's
         * visible on both mobile + desktop without crowding the search
         * + toggle bar below. The TextArea is locally buffered through
         * ``commentDraft`` so the parent's re-render churn (every
         * count commit refetches and bumps updated_at) doesn't wipe the
         * operator's in-flight typing. Commit on blur via
         * ``commitComment`` — that path serialises through the same
         * mutation queue + If-Match round-trip the counted_at editor
         * uses. Backend acceptance: ``PatchStocktakeRequest`` ignores
         * unknown fields today, so until the backend's request schema
         * grows a ``comment`` field this textarea's writes are silently
         * dropped; the local buffer still gives the operator a place to
         * jot during counting.
         *
         * autoSize {minRows:1,maxRows:3} keeps the row tight when empty
         * and grows up to ~3 lines for "morning count, two crates of
         * beer broken"-shaped notes without ballooning the header. */}
        <Row style={{ marginTop: 8 }}>
          <Col xs={24}>
            <Input.TextArea
              placeholder="Comment (optional) — operator note, e.g. 'morning count, two crates of beer broken'"
              value={commentDraft}
              autoSize={{ minRows: 1, maxRows: 3 }}
              disabled={editingDisabled}
              onFocus={() => {
                commentEditingRef.current = true;
              }}
              onChange={(e) => {
                commentEditingRef.current = true;
                setCommentDraft(e.target.value);
              }}
              onBlur={() => {
                commentEditingRef.current = false;
                void commitComment(commentDraft);
              }}
            />
          </Col>
        </Row>

        <Space
          style={{ width: "100%", marginTop: 8 }}
          wrap
          size={isMobile ? 8 : 12}
        >
          <Input.Search
            placeholder="Search articles…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ flex: 1, minWidth: 200 }}
          />
          <Space size={4}>
            <Text type="secondary">Tree:</Text>
            <Tooltip title="Group by category and product group">
              <Switch
                checked={treeView}
                onChange={setTreeView}
                size="small"
              />
            </Tooltip>
          </Space>
          {/* Hide-counted shrinks the worklist to what's still pending.
           * Defaults ON — the currently-active row stays visible via the
           * activeRoot exception in buildRows. */}
          <Space size={4}>
            <Text type="secondary">Hide counted:</Text>
            <Switch
              checked={hideCounted}
              onChange={setHideCounted}
              size="small"
            />
          </Space>
          {/* No-barcode section now lives behind a Collapse panel below
           * (§8.3 minor). The hide-toggle is gone — the panel itself
           * gives the operator a single click to show/hide. */}
          <Space size={4}>
            <Text type="secondary">Scanner:</Text>
            <Switch
              checked={scannerEnabled}
              onChange={setScannerEnabled}
              size="small"
            />
          </Space>
          <Space size={4}>
            <Text type="secondary">Beep:</Text>
            <Switch
              checked={feedbackEnabled}
              onChange={setFeedbackEnabled}
              size="small"
            />
          </Space>
          {/* Numpad layout toggle — calculator (7-8-9 top) vs phone
           * (1-2-3 top). Lives in the header row alongside the other
           * UI preferences so the operator can flip without leaving
           * the counting screen. */}
          <Space size={4}>
            <Text type="secondary">Numpad:</Text>
            <Segmented
              size="small"
              value={numpadLayout}
              onChange={(v) =>
                setNumpadLayout(v as "calculator" | "phone")
              }
              options={[
                { label: "Calc", value: "calculator" },
                { label: "Phone", value: "phone" },
              ]}
            />
          </Space>
          {/* Mobile-only: expression keypad (loose/crates/broken) vs the
           * bare single-number Numpad. Lives alongside the other input
           * prefs; has no effect on desktop (chip strip is always on). */}
          <Space size={4}>
            <Text type="secondary">Keypad:</Text>
            <Tooltip title="On: loose/crates/broken mode keypad. Off: plain number entry.">
              <Switch
                checked={expressionKeypadEnabled}
                onChange={setExpressionKeypadEnabled}
                size="small"
              />
            </Tooltip>
          </Space>
          <Space size={6}>
            <Button size="small" onClick={() => void refreshBootstrap()}>
              Refresh expected
            </Button>
            {/* Show the bootstrap snapshot timestamp so the operator
             * can tell at a glance how fresh the expected-qty values
             * are. ``Text type="secondary"`` is theme-aware — no
             * hard-coded colour to vanish in dark mode. */}
            {expected && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Refreshed {formatCompactDateTime(expected.snapshotAt)}
              </Text>
            )}
          </Space>
        </Space>
      </div>

      {staleBanner && (
        <Alert
          type="info"
          showIcon
          message="Someone else edited this — reload?"
          action={
            <Button size="small" type="primary" onClick={() => void refresh()}>
              Reload
            </Button>
          }
          style={{ marginBottom: 12 }}
        />
      )}
      {error && (
        <Alert
          type="error"
          closable
          onClose={() => setError(null)}
          message={error}
          style={{ marginBottom: 12 }}
        />
      )}
      {isFinalized && (
        <Alert
          type="info"
          showIcon
          message="This stocktake is finalized."
          description="Edits cascade to the linked Stock-loss expense row and stock_adjustment delta. Scanning is paused because new scans on a finalized count would be ambiguous."
          style={{ marginBottom: 12 }}
        />
      )}

      {/* ------------- Article list -------------
       *
       * §8.3: no-barcode pinned at the top inside a collapsible panel;
       * the main article table below stays expanded. */}
      {noBarcodeRows.length > 0 && (
        <Collapse
          activeKey={noBarcodeOpen ? ["no-barcode"] : []}
          onChange={(keys) =>
            setNoBarcodeOpen(
              Array.isArray(keys) ? keys.includes("no-barcode") : keys === "no-barcode",
            )
          }
          style={{ marginBottom: 4 }}
          items={[
            {
              key: "no-barcode",
              label: `No barcode (${noBarcodeRows.length})`,
              // Tree view re-uses renderMobileList on desktop too — it
              // already produces a workable card layout, and the antd
              // Table doesn't natively grok category/group nesting
              // without a much bigger refactor.
              children: (isMobile || treeView)
                ? renderMobileList(noBarcodeRows, "")
                : (
                  <Table
                    rowKey="root"
                    columns={rowColumns}
                    dataSource={noBarcodeRows}
                    pagination={false}
                    size="small"
                    onRow={(r) => ({
                      onClick: () => selectRow(r.root),
                      style: {
                        cursor: "pointer",
                        background: r.active
                          ? "rgba(24,144,255,0.08)"
                          : undefined,
                      },
                    })}
                  />
                ),
            },
          ]}
        />
      )}
      {(isMobile || treeView) ? (
        renderMobileList(withBarcodeRows, `Articles (${withBarcodeRows.length})`)
      ) : (
        <>
          <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
            Articles ({withBarcodeRows.length})
          </Title>
          <Table
            rowKey="root"
            columns={rowColumns}
            dataSource={withBarcodeRows}
            pagination={{ pageSize: 50, showSizeChanger: false }}
            size="small"
            onRow={(r) => ({
              onClick: () => selectRow(r.root),
              style: {
                cursor: "pointer",
                background: r.active ? "rgba(24,144,255,0.08)" : undefined,
              },
            })}
          />
        </>
      )}

      {/* §5 (add-pos-recipes-and-cups): per-volumetric-article open-container
          level capture. Pre-finalize it seeds the pour overlay's inter-count
          window; post-finalize it's a read-only record of what was measured. */}
      {row && (
        <div style={{ marginTop: 16 }}>
          <StocktakeOpenLevels
            lines={row.lines}
            articleByRoot={articleByRoot}
            nameByRoot={(id) => articleByRoot.get(id)?.name ?? `#${id}`}
            onCommit={commitMeasuredOpen}
            disabled={isFinalized || editingDisabled}
          />
        </div>
      )}

      {/* ------------- Finalize / Cancel / Delete CTAs -------------
       *
       * Pre-finalize: Finalize button + Cancel-stocktake (DELETE works
       * server-side even before finalize — the cascade is a no-op when
       * no expenses / adjustments exist yet).
       *
       * Post-finalize: read-only lines view + Delete-stocktake (which
       * cascades the linked Stock-loss expense row + stock_adjustment
       * delta — Pfand is not written at finalize, see module docstring).
       *
       * Both buttons re-use the same confirm modal with a ``mode``
       * marker so the copy stays in lock-step with what's actually
       * about to happen. */}
      {!isFinalized && (
        <Space style={{ marginTop: 16, flexWrap: "wrap" }}>
          <Button
            type="primary"
            size="large"
            onClick={() => void openPreview()}
            loading={previewBusy}
            disabled={editingDisabled}
          >
            Finalize…
          </Button>
          <Button
            danger
            disabled={editingDisabled}
            onClick={() => setDeleteMode("cancel-in-progress")}
          >
            Cancel stocktake
          </Button>
        </Space>
      )}
      {isFinalized && (
        <div style={{ marginTop: 16 }}>
          <FinalizedLinesView
            lines={row.lines}
            articleByRoot={articleByRoot}
            staleChainRootIds={stalePreview.staleRootIds}
          />
          <Space style={{ marginTop: 12 }} wrap>
            <Button
              danger
              onClick={() => setDeleteMode("delete-finalized")}
            >
              Delete stocktake
            </Button>
            <Tooltip
              title={
                stalePreview.staleRootIds.size === 0
                  ? "Nothing to re-anchor — every line's frozen expected_qty matches today's live values."
                  : "Re-anchor the frozen Expected snapshot for stale lines and re-derive the cascade."
              }
            >
              <Button
                disabled={stalePreview.staleRootIds.size === 0}
                onClick={() => setReanchorOpen(true)}
              >
                Re-anchor snapshot
                {stalePreview.staleRootIds.size > 0 && (
                  <Tag
                    color="orange"
                    style={{ marginLeft: 8, marginRight: 0 }}
                  >
                    {stalePreview.staleRootIds.size}
                  </Tag>
                )}
              </Button>
            </Tooltip>
          </Space>
          <ReanchorModal
            stocktakeId={row.id}
            ifMatch={row.updated_at ?? ""}
            open={reanchorOpen}
            onClose={() => setReanchorOpen(false)}
            onApplied={async () => {
              setReanchorOpen(false);
              await refresh();
              await refreshStalePreview();
            }}
          />
        </div>
      )}

      {/* ------------- Mobile bottom panel -------------
       *
       * Layout is consistent across collapsed + expanded:
       *
       *   - Left column: info view (article name + Exp/Cnt/Mis cells).
       *     When searchOpen, this is replaced by the AutoComplete and
       *     the "=" + Numpad block is hidden (search owns the surface).
       *   - Right column: [collapse, search] anchored to the TOP and
       *     the camera anchored to the BOTTOM (a flex spacer between
       *     them keeps both ends pinned regardless of left-column
       *     height).
       *   - Expanded ONLY: "=" button (full keypad width) + Numpad
       *     stacked between the info view and the camera. Camera stays
       *     in the bottom-right corner in both states — when collapsed
       *     it just naturally sits closer to the search button since
       *     there's no numpad pushing it down.
       *
       * Rendered post-finalize as well so the cascade-edit path stays
       * reachable from mobile. The numpad disables on offline / when no
       * row is active just like before. */}
      {isMobile && (() => {
        // Derive active row context for the big info line.
        // numpadValue is the user's in-flight typing; committed
        // counts.byRoot is the fallback for "what's actually saved".
        const activeArticle = activeRoot !== null ? articleByRoot.get(activeRoot) : null;
        const activeExpected = activeRoot !== null ? (expected?.byRoot.get(activeRoot) ?? null) : null;
        const activeExpectedNow = activeRoot !== null ? (expected?.nowByRoot.get(activeRoot) ?? null) : null;
        const activeCounted = (() => {
          if (activeRoot === null) return null;
          // When the expression keypad owns entry (expanded), show its LIVE
          // total so the centered Counted + Mismatch track typing instead of
          // jumping to the committed value only on each debounced submit.
          // Falls back to the committed value until the keypad reports.
          if (expressionKeypadEnabled && !keypadCollapsed) {
            return keypadLiveCount ?? counts.byRoot.get(activeRoot) ?? null;
          }
          if (numpadValue !== "") return Number(numpadValue);
          return counts.byRoot.get(activeRoot) ?? null;
        })();
        const activeDelta = (activeExpected !== null && activeCounted !== null)
          ? activeCounted - activeExpected
          : null;
        // Drift = how much the world has moved on since this line's
        // per-line counted_at. Negative = sales since count; positive
        // = (rare; refills are blocked during in-progress stocktakes,
        // but a manual stock_adjustment could surface here). Only
        // shown / actionable when ``activeCounted`` is non-null —
        // until the operator has typed/committed a count, there's
        // nothing to re-walk yet.
        const activeDriftSinceCount = (
          activeExpected !== null
          && activeExpectedNow !== null
          && activeCounted !== null
        )
          ? activeExpectedNow - activeExpected
          : null;
        // The "=" button auto-fills the expected qty into the counted
        // input. Negative expected (theoretical: an over-sold article
        // before a refill catches up) would commit a negative qty
        // through onNumpadChange's parseFloat path; clamp at 0 so the
        // button is always a safe one-tap "I believe the system".
        const targetCountedFromEq =
          activeExpected !== null ? Math.max(0, activeExpected) : null;
        return (
          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              background: "var(--ant-color-bg-elevated)",
              borderTop:
                "1px solid var(--ant-color-border-secondary, rgba(0,0,0,0.1))",
              padding: 8,
              zIndex: 20,
            }}
          >
            <div style={{ display: "flex", gap: 8 }}>
              {/* Left flexible column: info view + (when expanded)
               * the "=" button + numpad. Replaced wholesale by the
               * AutoComplete when searchOpen is true. */}
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                {searchOpen ? (
                  // Search mode: AutoComplete replaces the info view.
                  // placement="topLeft" opens the suggestion list UPWARD
                  // so it grows away from the OS keyboard (which slides
                  // up from the bottom on mobile and would otherwise
                  // occlude the dropdown). The "=" + Numpad block is
                  // implicitly hidden because we don't render it in this
                  // branch.
                  <AutoComplete
                    autoFocus
                    value={searchValue}
                    onChange={setSearchValue}
                    onSelect={(value: string) => {
                      const articleId = Number(value);
                      if (!Number.isNaN(articleId)) selectRow(articleId);
                      setSearchOpen(false);
                      setSearchValue("");
                    }}
                    options={Array.from(articleByRoot.values())
                      .filter(
                        (a) =>
                          searchValue === "" ||
                          a.name
                            .toLowerCase()
                            .includes(searchValue.toLowerCase()),
                      )
                      .slice(0, 12)
                      .map((a) => ({
                        value: String(a.chain_root_id),
                        label: a.name,
                      }))}
                    placeholder="Search articles…"
                    style={{ width: "100%" }}
                    placement="topLeft"
                    allowClear
                  />
                ) : (
                  <>
                    {/* Info view — read-only. Always rendered (both
                     * collapsed and expanded) so the operator can see
                     * Exp/Cnt/Mis at a glance even with the numpad hidden.
                     * Everything is full-width so the Exp/Cnt/Mis row and
                     * the "=" button line up edge-for-edge with the numpad
                     * grid below (which spans the whole left column). */}
                    <div>
                      <Text
                        strong
                        style={{
                          // Active article in the info bar gets more
                          // visual weight than the list rows so the
                          // operator can read the current row at a
                          // glance without scanning for the highlight.
                          // overflow/textOverflow/whiteSpace already
                          // truncate with an ellipsis when the article
                          // name is wider than the column.
                          fontSize: 27, // 1.5x — prominent active-row title
                          lineHeight: 1.1,
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {activeArticle?.name ?? "Tap a row to count"}
                      </Text>
                      <div>
                        <Row gutter={8} align="middle" style={{ marginTop: 0 }}>
                          <Col span={7} style={{ textAlign: "left" }}>
                            <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Expected</Text>
                            <Text style={{ fontSize: 34, fontWeight: 800, lineHeight: 1, display: "block" }}>{activeExpected ?? "—"}</Text>
                          </Col>
                          <Col span={10} style={{ textAlign: "center" }}>
                            <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Counted</Text>
                            <Text style={{ fontSize: 44, fontWeight: 800, lineHeight: 1, display: "block" }}>{activeCounted === null ? "—" : activeCounted}</Text>
                          </Col>
                          <Col span={7} style={{ textAlign: "right" }}>
                            <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Mismatch</Text>
                            <Text style={{
                              fontSize: 34,
                              fontWeight: 800,
                              lineHeight: 1,
                              display: "block",
                              color: activeDelta === null
                                ? undefined
                                : activeDelta === 0 ? "#52c41a" : "#ff4d4f",
                            }}>
                              {activeDelta === null ? "—" : (activeDelta > 0 ? `+${activeDelta}` : String(activeDelta))}
                            </Text>
                          </Col>
                        </Row>
                        {/* Drift indicator: appears only when the
                         * world has moved on since the operator's
                         * first-stamp moment. Secondary text, small
                         * (~12px) so it doesn't compete with the
                         * Exp/Cnt/Mis row above. */}
                        {activeDriftSinceCount !== null && activeDriftSinceCount !== 0 && (
                          <Text
                            type="secondary"
                            style={{ fontSize: 12, display: "block", marginTop: 2 }}
                          >
                            since: {activeDriftSinceCount > 0
                              ? `+${activeDriftSinceCount}`
                              : String(activeDriftSinceCount)}
                          </Text>
                        )}
                        {/* Expanded only: ``= N`` (auto-fill with
                         * expected@count_at) + ``↻ N`` (re-walk:
                         * commit the live-now value AND restamp the
                         * line's counted_at to NOW). The ↻ only
                         * appears when there is actual drift —
                         * otherwise = and ↻ would be identical. */}
                        {!keypadCollapsed && (() => {
                          const hasDrift =
                            activeDriftSinceCount !== null
                            && activeDriftSinceCount !== 0
                            && activeExpectedNow !== null;
                          const equalsDisabled =
                            editingDisabled
                            || activeRoot === null
                            || targetCountedFromEq === null;
                          const restampDisabled =
                            editingDisabled
                            || activeRoot === null
                            || activeExpectedNow === null;
                          const restampValue = hasDrift
                            ? Math.max(0, activeExpectedNow ?? 0)
                            : null;
                          return (
                            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                              <Button
                                type="primary"
                                size="large"
                                disabled={equalsDisabled}
                                onClick={() =>
                                  targetCountedFromEq !== null &&
                                  onNumpadChange(String(targetCountedFromEq))
                                }
                                style={{
                                  flex: hasDrift ? 1 : "1 1 100%",
                                  fontSize: 30,
                                  fontWeight: 600,
                                }}
                              >
                                = {targetCountedFromEq !== null ? targetCountedFromEq : ""}
                              </Button>
                              {hasDrift && activeRoot !== null && restampValue !== null && (() => {
                                // Audit-fix MAJOR: the label MUST show
                                // the value the click will commit, not
                                // the system-trust default — otherwise
                                // the operator can type 8, see "↻ 9",
                                // tap it, and the ledger records 8
                                // (their typed value wins). Resolve
                                // the commit value at render time and
                                // use it for BOTH the label and the
                                // handler so they cannot diverge.
                                const numpadParsed =
                                  numpadValue !== ""
                                    ? Number(numpadValue)
                                    : null;
                                const commitVal =
                                  numpadParsed !== null
                                  && Number.isFinite(numpadParsed)
                                    ? Math.round(numpadParsed)
                                    : restampValue;
                                return (
                                  <Button
                                    // Keying off ``activeRoot`` (not
                                    // commitVal) makes the flash fire
                                    // when the operator selects/scans
                                    // a row that happens to have
                                    // drift, but doesn't re-fire as
                                    // they type into the numpad.
                                    key={`restamp-${activeRoot}`}
                                    type="default"
                                    size="large"
                                    disabled={restampDisabled}
                                    onClick={() => {
                                      void commitCountWithRestamp(
                                        activeRoot,
                                        commitVal,
                                      );
                                      setNumpadValue("");
                                    }}
                                    className="restamp-flash"
                                    style={{
                                      flex: 1,
                                      fontSize: 26,
                                      fontWeight: 600,
                                    }}
                                    aria-label={`Re-walked, set counted to ${commitVal}`}
                                  >
                                    ↻ {commitVal}
                                  </Button>
                                );
                              })()}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    {!keypadCollapsed && (
                      activeRoot !== null && expressionKeypadEnabled ? (
                        <CountExpressionKeypad
                          // Key by the active row so each row gets a fresh
                          // buffer seeded from its own committed terms; our
                          // own server echoes (same key) never remount it.
                          key={activeRoot}
                          terms={countExpressionByRoot.get(activeRoot) ?? null}
                          packSize={
                            articleByRoot.get(activeRoot)?.pack_size ?? null
                          }
                          disabled={editingDisabled}
                          onCommit={(terms, countedQty) =>
                            onTermsEdit(activeRoot, terms, countedQty)
                          }
                          onLiveTotal={setKeypadLiveCount}
                        />
                      ) : (
                        <Numpad
                          value={numpadValue}
                          onChange={onNumpadChange}
                          layout={numpadLayout}
                          disabled={editingDisabled || activeRoot === null}
                        />
                      )
                    )}
                  </>
                )}
              </div>
              {/* Right column: stacked auxiliary buttons.
               *
               * Collapsed state shows only: collapse, search, camera —
               * ⌫ and C are useless when the numpad is hidden, so we
               * drop them along with the flex spacer to keep the column
               * tight. Camera sits directly below search.
               *
               * Expanded state shows: collapse, search, ⌫, C, [spacer],
               * camera — the spacer pushes the camera to the bottom-
               * right corner so it visually anchors to the bottom of
               * the numpad.
               *
               * Camera button is ALWAYS rendered on mobile (no
               * BarcodeDetector feature gate at this layer — the useEffect
               * driving the scanner surfaces an explicit error if the
               * browser/context doesn't support it; see the camera
               * lifecycle effect above). */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  flex: "none",
                }}
              >
                {/* All right-column buttons share the Numpad's 44×44
                 * button footprint so the column visually aligns with
                 * the numpad rows. */}
                <Button
                  icon={keypadCollapsed ? <UpOutlined /> : <DownOutlined />}
                  onClick={() => setKeypadCollapsed((c) => !c)}
                  aria-label={
                    keypadCollapsed ? "Expand keypad" : "Collapse keypad"
                  }
                  style={{ width: 44, height: 44 }}
                />
                <Button
                  type={searchOpen ? "primary" : "default"}
                  icon={<SearchOutlined />}
                  onClick={() => {
                    const wasOpen = searchOpen;
                    setSearchOpen(!wasOpen);
                    if (wasOpen) setSearchValue("");
                  }}
                  style={{ width: 44, height: 44 }}
                />
                {/* ⌫ + C are meaningless in search mode (the numpad is
                    replaced by the AutoComplete) and when the keypad is
                    collapsed — hide them in both. */}
                {!keypadCollapsed && !searchOpen && (
                  <>
                    {/* ⌫ — delete last digit. Operates on the legacy
                     * Numpad's ``numpadValue`` buffer, so it's only
                     * meaningful when the expression keypad is OFF — the
                     * CountExpressionKeypad carries its own ⌫. Hiding it
                     * when the keypad is on avoids a confusing dead
                     * duplicate. Disabled when there's no active row. */}
                    {!expressionKeypadEnabled && (
                      <Button
                        type="primary"
                        onClick={() => onNumpadChange(numpadValue.slice(0, -1))}
                        disabled={editingDisabled || activeRoot === null}
                        aria-label="Backspace"
                        style={{
                          width: 44,
                          height: 132, // 3× — large, easy backspace target
                          fontSize: 18,
                          fontWeight: 500,
                        }}
                      >
                        ⌫
                      </Button>
                    )}
                    {/* × — fully UNCOUNT the active row (delete the line →
                     * back to "uncounted"), not commit a literal 0. Danger
                     * style mirrors the desktop × icon button. Renamed
                     * from "C" so mobile + desktop share the same glyph
                     * for the same destructive verb. */}
                    <Button
                      danger
                      onClick={() => uncountActive()}
                      disabled={editingDisabled || activeRoot === null}
                      aria-label="Uncount this row"
                      style={{
                        width: 44,
                        height: 44,
                        fontSize: 22,
                        fontWeight: 500,
                      }}
                    >
                      ×
                    </Button>
                    <div style={{ flex: 1 }} />
                  </>
                )}
                <Button
                  type="primary"
                  icon={<CameraOutlined />}
                  onClick={() => setCameraOpen((o) => !o)}
                  style={{ width: 44, height: 44 }}
                  aria-label="Camera scan"
                />
              </div>
            </div>
          </div>
        );
      })()}

      {/* ------------- Finalize preview modal ------------- */}
      <Modal
        open={preview !== null}
        title={`Finalize stocktake #${row.id}`}
        onCancel={() => setPreview(null)}
        width={760}
        footer={
          <Space>
            <Button onClick={() => setPreview(null)} disabled={previewBusy}>
              Back to counting
            </Button>
            <Button
              type="primary"
              danger
              loading={previewBusy}
              disabled={(preview?.blockers?.length ?? 0) > 0}
              onClick={() => void confirmFinalize()}
            >
              Confirm finalize
            </Button>
          </Space>
        }
      >
        {preview && (
          <FinalizePreviewBody
            proposal={preview}
            articleByRoot={articleByRoot}
            onAfterFix={refreshPreview}
            onOpenInCatalog={(crId) => navigate(`/catalog/articles/${crId}`)}
            onOpenProcurement={(refillId) =>
              navigate(`/stock/procurement/${refillId}`)
            }
          />
        )}
      </Modal>

      {/* ------------- Unknown-barcode modal -------------
       *
       * The modal renders the HEAD of the unknownBarcode queue.
       * onResolved / onCancel shift the head off so the next pending
       * barcode (if any) takes over without re-mounting the modal. A
       * queue-depth chip below shows when more are waiting. */}
      <UnknownBarcodeModal
        open={unknownBarcode.length > 0}
        barcode={unknownBarcode[0] ?? null}
        queueDepth={unknownBarcode.length}
        articles={articles}
        categories={categories}
        groups={groups}
        onResolved={() => {
          const code = unknownBarcode[0] ?? null;
          // Pop the head — leave any trailing pending scans intact.
          setUnknownBarcode((prev) => prev.slice(1));
          if (code) {
            // Re-fire the scan with a fresh idempotency key so the new
            // barcode lookup hits the just-attached row.
            void fireScan(code);
          }
          // Refetch the articles list so the new barcode (and any new
          // article) lands in our autocomplete map.
          void api
            .listArticles({ active: true })
            .then((next) => setArticles(next))
            .catch(() => undefined);
        }}
        onCancel={() => setUnknownBarcode((prev) => prev.slice(1))}
      />

      {/* ------------- Camera scanner modal -------------
       *
       * Chrome-Android-only (gated by ``cameraSupported``). The
       * BarcodeDetector polls the live video at ~5Hz; on a hit we fire
       * a scan and auto-close. The unknown-barcode queue takes over
       * after that with its own modal — we just flash red+shake here as
       * a visual ack. */}
      <Modal
        open={cameraOpen}
        onCancel={() => setCameraOpen(false)}
        footer={null}
        title="Scan barcode"
        width="90%"
        destroyOnHidden
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            background: "black",
            borderRadius: 8,
            overflow: "hidden",
            outline:
              scanFlash === "unknown"
                ? "4px solid #ff4d4f"
                : scanFlash === "ok"
                  ? "4px solid #52c41a"
                  : "none",
            animation:
              scanFlash === "unknown" ? "stockTakeShake 0.7s" : undefined,
          }}
        >
          <video
            ref={videoRef}
            playsInline
            muted
            style={{ width: "100%", height: "auto", display: "block" }}
          />
        </div>
        <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
          Point at a barcode. Tap the camera button or close to stop.
        </Text>
      </Modal>

      {/* ------------- Offline modal ------------- */}
      <Modal
        open={offline !== null}
        title="Connection lost"
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={
          <Space>
            <Button
              type="primary"
              onClick={() => {
                if (!offline) return;
                const retry = offline.retry;
                setOffline(null);
                void retry();
              }}
            >
              Retry
            </Button>
            <Button
              onClick={() => {
                setOffline(null);
                void refresh();
              }}
            >
              Reload page state
            </Button>
          </Space>
        }
      >
        <p>
          The last scan or count update couldn't reach the server. The
          counts already on the server are preserved. Scanning is
          paused until you reconnect.
        </p>
      </Modal>

      {/* ------------- Delete / cancel-stocktake modal -------------
       *
       * Same Modal shell for both flows. Pre-finalize: copy points out
       * that no expenses / adjustments exist yet, so this discards
       * only the in-progress counts. Post-finalize: copy warns about
       * the cascade onto linked expense rows. */}
      <Modal
        open={deleteOpen}
        title={
          deleteMode === "cancel-in-progress"
            ? `Discard stocktake #${row.id}?`
            : `Delete stocktake #${row.id}?`
        }
        onCancel={() => {
          setDeleteMode(null);
          setDeleteText("");
        }}
        onOk={() => void confirmDelete()}
        okText={deleteMode === "cancel-in-progress" ? "Discard" : "Delete"}
        // Match the trim-aware comparator in confirmDelete so a
        // stray leading/trailing space doesn't disable the OK button
        // when the handler would otherwise accept the input.
        okButtonProps={{
          danger: true,
          disabled: String(row.id) !== deleteText.trim(),
        }}
      >
        {deleteMode === "cancel-in-progress" ? (
          <p>
            Discarding stocktake #{row.id} will permanently delete its
            in-progress counts (no expenses or adjustments are written
            yet). Type the id to confirm.
          </p>
        ) : (
          <>
            <p>
              This will hard-delete the stocktake and the linked
              Stock-loss expense row + stock_adjustment delta. The
              audit log keeps the forensic trail.
            </p>
            <ManualEditedExpensesWarning
              stocktakeId={row.id}
              open={deleteMode === "delete-finalized"}
            />
          </>
        )}
        <p>
          Type <Text code>{row.id}</Text> to confirm.
        </p>
        <Input
          value={deleteText}
          onChange={(e) => setDeleteText(e.target.value)}
          placeholder={String(row.id)}
        />
      </Modal>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Finalize preview body (§8.6)
// ---------------------------------------------------------------------------

const FinalizePreviewBody = ({
  proposal,
  articleByRoot,
  onAfterFix,
  onOpenInCatalog,
  onOpenProcurement,
}: {
  proposal: FinalizeProposal;
  articleByRoot: Map<number, ArticleRow>;
  /** Called after a blocker is fixed inline so the parent re-fetches
   * the preview and the blocker drops off this alert. */
  onAfterFix: () => Promise<void>;
  /** Navigate the operator to the catalog detail for a chain root —
   * used as the fallback when a blocker isn't fixable inline (e.g.
   * an unknown reason that the inline UI doesn't recognise). */
  onOpenInCatalog: (chainRootId: number) => void;
  /** Navigate the operator to the procurement detail page for a draft
   * refill surfaced in the soft-warning list. */
  onOpenProcurement: (refillId: number) => void;
}) => {
  // Defensive ?? [] across warnings/blockers/lines: a stale backend
  // (e.g. one that predates the warnings field) can omit any of these
  // arrays. Without the guard FinalizePreviewBody would throw
  // ``Cannot read properties of undefined (reading 'map')`` on mount and
  // blank the entire page — the operator would lose the modal AND the
  // counting list behind it. The guards keep the preview body renderable
  // regardless of which field the stale backend omits.
  const nonZero = (proposal.lines ?? []).filter((l) => l.mismatch_qty !== 0);
  return (
    <div>
      {/* Soft, non-blocking warnings. Rendered ABOVE blockers so the
       * operator sees them first — they're the "did you mean to do
       * this?" nudge, not a stop-the-line condition. Finalize stays
       * enabled even when warnings are present. */}
      {/* Round-2-pass-2 audit MAJOR: per-line soft warnings (buy_price
       * fallback when the per-line counted_at predates/postdates the
       * article's known buy_price effective_from window). Renders as a
       * single yellow Alert with one bullet per affected chain root so
       * the operator can spot non-canonical valuations before confirm. */}
      {(proposal.soft_warnings ?? []).length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Buy-price soft fallback in use"
          description={
            <ul style={{ margin: "4px 0 0 0", paddingLeft: 18 }}>
              {(proposal.soft_warnings ?? []).map((sw) => (
                <li key={sw.chain_root_id}>
                  Chain root #{sw.chain_root_id}:{" "}
                  {sw.code === "buy_price_predates_anchor_fallback"
                    ? "price predates count — used earliest known price"
                    : sw.code === "buy_price_postdates_anchor_fallback"
                      ? "all prices expired before count — used earliest known price"
                      : sw.code}
                  {sw.detail ? ` (${sw.detail})` : ""}
                </li>
              ))}
            </ul>
          }
        />
      )}
      {(proposal.warnings ?? []).map((w) => (
        <Alert
          key={w.code}
          type="warning"
          showIcon
          message={w.message}
          description={
            w.code === "draft_refills_overlap_counted_at" &&
            w.refills.length > 0 ? (
              <ul style={{ margin: "4px 0 0 0", paddingLeft: 18 }}>
                {w.refills.map((r) => (
                  <li key={r.id}>
                    <a
                      href={`/stock/procurement/${r.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        onOpenProcurement(r.id);
                      }}
                    >
                      Procurement #{r.id}
                    </a>
                    {r.supplier ? ` — ${r.supplier}` : ""}
                    {r.delivery_date ? ` — delivery ${r.delivery_date}` : ""}
                    {` — ${r.line_count} line${r.line_count === 1 ? "" : "s"}`}
                  </li>
                ))}
              </ul>
            ) : undefined
          }
          style={{ marginBottom: 12 }}
        />
      ))}
      {(proposal.blockers ?? []).length > 0 && (
        <Alert
          type="error"
          showIcon
          message={`${(proposal.blockers ?? []).length} blocker(s) — finalize is disabled until fixed`}
          description={
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              {(proposal.blockers ?? []).map((b) => (
                <BlockerRow
                  key={b.chain_root_id}
                  blocker={b}
                  articleName={
                    articleByRoot.get(b.chain_root_id)?.name ??
                    `#${b.chain_root_id}`
                  }
                  onAfterFix={onAfterFix}
                  onOpenInCatalog={onOpenInCatalog}
                />
              ))}
            </Space>
          }
          style={{ marginBottom: 16 }}
        />
      )}
      {/* Pfand explanatory banner — the wire still carries
       * proposed_pfand_value_cents + total_pfand_cents (always 0 from
       * 2026-06-04) but the UI no longer surfaces them as an expense
       * column. See api/client.ts JSDoc on the field for the
       * "stays on-site" rationale. */}
      <Alert
        type="info"
        showIcon
        banner
        style={{ marginBottom: 16 }}
        message="Pfand stays on-site — bottle deposits are recovered at the festival close-out via a manual return, or per-delivery on the procurement detail page. This stocktake does not write any Pfand expense rows."
      />
      <Space size="large" wrap style={{ marginBottom: 16 }}>
        <Text>
          Stock loss: <Text strong>{formatCents(proposal.total_stock_loss_cents)}</Text>
        </Text>
        {/* 0051: render the Stock-broken total alongside Stock-loss
            whenever it's non-zero. Zero is the common case (every line
            is pure shrinkage); rendering "Stock broken: €0,00" then
            would just be visual noise. */}
        {proposal.total_stock_broken_cents !== 0 && (
          <Text>
            Stock broken:{" "}
            <Text strong>
              {formatCents(proposal.total_stock_broken_cents)}
            </Text>
          </Text>
        )}
        <Text>
          Adjustments: <Text strong>{proposal.adjustment_count}</Text>
        </Text>
      </Space>
      <Table
        size="small"
        rowKey="chain_root_id"
        dataSource={nonZero}
        pagination={false}
        columns={[
          {
            title: "Article",
            dataIndex: "chain_root_id",
            render: (id: number) =>
              articleByRoot.get(id)?.name ?? `#${id}`,
          },
          { title: "Expected", dataIndex: "expected_qty", align: "right", width: 100 },
          { title: "Counted", dataIndex: "counted_qty", align: "right", width: 100 },
          { title: "Mismatch", dataIndex: "mismatch_qty", align: "right", width: 100 },
          {
            title: "Stock loss",
            dataIndex: "proposed_stock_loss_value_cents",
            render: formatCents,
            align: "right",
            width: 120,
          },
          {
            // 0051: paired Stock-broken column. Renders the same
            // negative-cents formatting as Stock loss so the operator
            // reads "€0,00" for the common case (no broken terms)
            // without needing to hunt for an empty cell.
            title: "Stock broken",
            dataIndex: "proposed_stock_broken_value_cents",
            render: formatCents,
            align: "right",
            width: 120,
          },
        ]}
      />
    </div>
  );
};

/** One row inside the blocker alert.
 *
 * Detects whether the blocker is fixable inline (buy_price-shaped
 * reasons) and renders an InputNumber + Save button if so. The
 * detection is intentionally loose — the backend reason is a free-form
 * string today, so we substring-match for "buy_price" / "buy price".
 * Anything else falls back to an "Open in catalog" link button so the
 * operator still has a one-click recovery path.
 */
const BlockerRow = ({
  blocker,
  articleName,
  onAfterFix,
  onOpenInCatalog,
}: {
  blocker: { chain_root_id: number; reason: string };
  articleName: string;
  onAfterFix: () => Promise<void>;
  onOpenInCatalog: (chainRootId: number) => void;
}) => {
  const [buyPrice, setBuyPrice] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const isBuyPriceBlocker = /buy.?price/i.test(blocker.reason);

  const saveBuyPrice = async () => {
    if (buyPrice === null || !Number.isFinite(buyPrice) || buyPrice <= 0) {
      setLocalError("Enter a buy price greater than zero.");
      return;
    }
    setSaving(true);
    setLocalError(null);
    try {
      await api.setArticleBuyPrice(blocker.chain_root_id, {
        buy_price_cents: Math.round(buyPrice),
      });
      // Re-fetch the preview so this blocker drops off the alert.
      await onAfterFix();
    } catch (err) {
      setLocalError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Space wrap size={8} align="center" style={{ width: "100%" }}>
        <Text strong>{articleName}</Text>
        <Text type="secondary">— {blocker.reason}</Text>
        {isBuyPriceBlocker ? (
          <>
            <InputNumber
              size="small"
              min={1}
              // Buy price in cents (integer) — digits-only keypad on mobile.
              // This is a normal fix-up input (not the count keypad), so the
              // OS numeric keypad is wanted here.
              inputMode="numeric"
              value={buyPrice ?? undefined}
              onChange={(v) => setBuyPrice(v as number | null)}
              addonAfter="ct"
              placeholder="buy price"
              disabled={saving}
            />
            <Button
              size="small"
              type="primary"
              loading={saving}
              onClick={() => void saveBuyPrice()}
            >
              Save buy price
            </Button>
          </>
        ) : (
          <Button
            size="small"
            onClick={() => onOpenInCatalog(blocker.chain_root_id)}
          >
            Open in catalog
          </Button>
        )}
      </Space>
      {localError && (
        <Text type="danger" style={{ display: "block", fontSize: 12 }}>
          {localError}
        </Text>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Finalized lines summary (§8.7)
// ---------------------------------------------------------------------------

const FinalizedLinesView = ({
  lines,
  articleByRoot,
  staleChainRootIds,
}: {
  lines: StocktakeLineRow[];
  articleByRoot: Map<number, ArticleRow>;
  /** Chain roots whose ``expected_qty_at_finalize`` snapshot is now
   * out of sync with today's live values (procurement, adjustments
   * or sales were corrected post-finalize). The "Expected (snap)"
   * cell badges these so the operator sees the audit trail is stale
   * before they click "Re-anchor snapshot". */
  staleChainRootIds: Set<number>;
}) => {
  // Render every line — earlier we filtered to lines with a non-null
  // ``expected_qty_at_finalize`` snapshot, but that hid the entire view
  // when finalize ran without bootstrap data populated (every snapshot
  // null → rows.length === 0 → return null → "empty page"). Show all
  // lines and render "—" for missing snapshot fields instead.
  const rows = useMemo(
    () =>
      lines.map((l) => ({
        ...l,
        mismatch:
          l.expected_qty_at_finalize !== null &&
          l.expected_qty_at_finalize !== undefined
            ? l.counted_qty - l.expected_qty_at_finalize
            : null,
      })),
    [lines],
  );
  if (rows.length === 0) {
    return (
      <Empty
        description="No counted lines recorded."
        style={{ marginBottom: 16 }}
      />
    );
  }
  const renderEmDash = (v: number | null | undefined) =>
    v === null || v === undefined ? "—" : v;
  return (
    <Table
      size="small"
      rowKey="article_chain_root_id"
      dataSource={rows}
      pagination={false}
      style={{ marginBottom: 16 }}
      columns={[
        {
          title: "Article",
          dataIndex: "article_chain_root_id",
          render: (id: number) =>
            articleByRoot.get(id)?.name ?? `#${id}`,
        },
        {
          title: "Expected (snap)",
          dataIndex: "expected_qty_at_finalize",
          align: "right",
          width: 150,
          render: (v: number | null | undefined, r) => {
            const isStale = staleChainRootIds.has(r.article_chain_root_id);
            if (v === null || v === undefined) {
              return isStale ? (
                <Tooltip title="Frozen snapshot is stale — procurement, adjustments or sales were corrected after this stocktake was finalized. Use 'Re-anchor snapshot' to refresh.">
                  <Tag color="orange" style={{ marginRight: 0 }}>—</Tag>
                </Tooltip>
              ) : (
                "—"
              );
            }
            return isStale ? (
              <Tooltip title="Frozen snapshot is stale — procurement, adjustments or sales were corrected after this stocktake was finalized. Use 'Re-anchor snapshot' to refresh.">
                <span>
                  {v}{" "}
                  <Tag color="orange" style={{ marginRight: 0 }}>stale</Tag>
                </span>
              </Tooltip>
            ) : (
              v
            );
          },
        },
        { title: "Counted", dataIndex: "counted_qty", align: "right", width: 100 },
        {
          title: "Mismatch",
          dataIndex: "mismatch",
          align: "right",
          width: 100,
          render: renderEmDash,
        },
        {
          title: "Buy price (snap)",
          dataIndex: "buy_price_cents_at_finalize",
          align: "right",
          width: 130,
          render: formatCents,
        },
        {
          title: "Pfand (snap)",
          dataIndex: "pfand_value_cents_at_finalize",
          align: "right",
          width: 110,
          render: formatCents,
        },
      ]}
    />
  );
};

// ---------------------------------------------------------------------------
// Delete-modal manual-edits warning (§8.7)
// ---------------------------------------------------------------------------

const ManualEditedExpensesWarning = ({
  stocktakeId,
  open,
}: {
  stocktakeId: number;
  /** Only re-fetch when the parent modal opens — otherwise we burn a
   * round-trip on every detail-page render. */
  open: boolean;
}) => {
  // §8.7 minor — compute the count client-side by listing all expenses
  // linked to this stocktake and counting rows where ``updated_at >
  // created_at + small jitter``. The 1s threshold absorbs the
  // create/refresh round-trip that the backend stamps both fields in.
  // The backend doesn't yet surface a "manually_edited_count" field on
  // the stocktake detail; this client-side compute mirrors what such a
  // field would do without a schema change.
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void api
      .listExpenses({ limit: 500 })
      .then((rows) => {
        if (cancelled) return;
        const linked = rows.filter((r) => r.linked_stocktake_id === stocktakeId);
        const edited = linked.filter((r) => {
          const dCreated = Date.parse(r.created_at);
          const dUpdated = Date.parse(r.updated_at);
          if (!Number.isFinite(dCreated) || !Number.isFinite(dUpdated)) return false;
          return dUpdated - dCreated > 1000;
        });
        setCount(edited.length);
      })
      .catch(() => {
        // Best-effort — fall back to the generic copy below.
        setCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [stocktakeId, open]);
  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 16 }}
      message={
        count === null
          ? "Any manual edits to the linked Stock-loss expense row will be lost."
          : count === 0
            ? "No linked expense rows have been manually edited since finalize."
            : `${count} linked expense row(s) have been manually edited since finalize — those edits will be lost.`
      }
    />
  );
};

// ---------------------------------------------------------------------------
// Re-anchor modal — refreshes the frozen ``expected_qty_at_finalize``
// snapshot on a finalized stocktake when procurement, adjustments or
// sales were corrected post-finalize. Two-step UX: a warning step the
// operator MUST acknowledge, then the per-line preview with checkboxes
// to opt rows out. Apply call is one atomic POST; the page refreshes
// on success.
// ---------------------------------------------------------------------------

const ReanchorModal = ({
  stocktakeId,
  ifMatch,
  open,
  onClose,
  onApplied,
}: {
  stocktakeId: number;
  ifMatch: string;
  open: boolean;
  onClose: () => void;
  onApplied: () => void | Promise<void>;
}) => {
  type Step = "warning" | "preview" | "applying";
  const [step, setStep] = useState<Step>("warning");
  const [preview, setPreview] = useState<ReanchorPreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [skipped, setSkipped] = useState<Set<number>>(new Set());

  // Reset when the modal opens.
  useEffect(() => {
    if (!open) return;
    setStep("warning");
    setPreview(null);
    setError(null);
    setSkipped(new Set());
  }, [open]);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = await api.reanchorStocktakePreview(stocktakeId);
      setPreview(p);
      setStep("preview");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load preview.");
    } finally {
      setLoading(false);
    }
  }, [stocktakeId]);

  const apply = useCallback(async () => {
    setStep("applying");
    setError(null);
    try {
      // Round-2-pass-2 audit CRITICAL #3: echo the preview signature
      // so the server detects live-state drift between modal-open
      // and apply. On 409 ``preview_drift``, refetch and re-render
      // the preview step with an inline banner.
      await api.reanchorStocktakeApply(
        stocktakeId,
        Array.from(skipped),
        ifMatch,
        preview?.preview_signature,
      );
      await onApplied();
    } catch (e) {
      if (e instanceof ApiError && e.errorCode === "preview_drift") {
        setError(
          "Live stock state changed since the preview was rendered. " +
            "Refresh the preview and confirm again.",
        );
        // Refetch to surface the new numbers.
        await loadPreview();
        setStep("preview");
        return;
      }
      setError(e instanceof Error ? e.message : "Re-anchor failed.");
      setStep("preview");
    }
  }, [stocktakeId, ifMatch, skipped, onApplied, preview, loadPreview]);

  const toggleSkip = useCallback((chainRootId: number) => {
    setSkipped((prev) => {
      const next = new Set(prev);
      if (next.has(chainRootId)) {
        next.delete(chainRootId);
      } else {
        next.add(chainRootId);
      }
      return next;
    });
  }, []);

  const numberCell = (v: number) => v.toString();
  const previewColumns: ColumnsType<ReanchorLineDiffRow> = [
    {
      title: "Apply",
      key: "apply",
      width: 70,
      render: (_: unknown, r) => (
        <Switch
          checked={!skipped.has(r.chain_root_id)}
          onChange={() => toggleSkip(r.chain_root_id)}
          size="small"
        />
      ),
    },
    {
      title: "Article",
      dataIndex: "article_name",
      render: (v: string | null, r) => v ?? `#${r.chain_root_id}`,
    },
    {
      title: "Expected (was → now)",
      key: "expected",
      width: 200,
      render: (_: unknown, r) => (
        <span>
          <Text type="secondary" delete>
            {numberCell(r.frozen_expected_qty)}
          </Text>{" "}
          → <Text strong>{numberCell(r.new_expected_qty)}</Text>
        </span>
      ),
    },
    {
      title: "Counted",
      dataIndex: "counted_qty",
      width: 90,
      align: "right",
    },
    {
      title: "Adjustment (was → now)",
      key: "adjustment",
      width: 210,
      render: (_: unknown, r) => {
        const before = r.frozen_adjustment_delta;
        const after = r.new_adjustment_delta;
        return (
          <span>
            <Text type="secondary" delete>{before}</Text>
            {" "}
            → <Text strong>{after}</Text>
          </span>
        );
      },
    },
    {
      // Round-3 audit MAJOR #3: surface frozen vs new buy_price per
      // row so a bp-drift-only line (e.g. a retroactive procurement
      // unit-cost correction) isn't an unexplained value-cents shift
      // in the Loss-expense column.
      title: "Buy price (was → now)",
      key: "buy_price",
      width: 180,
      render: (_: unknown, r) => {
        const frozen = r.frozen_buy_price_cents ?? null;
        const next = r.new_buy_price_cents ?? null;
        if (frozen === null && next === null) {
          return <Text type="secondary">—</Text>;
        }
        if (frozen === next) {
          return <Text type="secondary">{formatCents(frozen ?? 0)}</Text>;
        }
        return (
          <span>
            <Text type="secondary" delete>
              {frozen === null ? "—" : formatCents(frozen)}
            </Text>{" "}
            → <Text strong>{next === null ? "—" : formatCents(next)}</Text>
            {r.is_bp_stale && (
              <div>
                <Tag color="orange" style={{ marginTop: 2 }}>
                  bp drift
                </Tag>
              </div>
            )}
          </span>
        );
      },
    },
    {
      title: "Loss expense (was → now)",
      key: "loss",
      width: 260,
      render: (_: unknown, r) => {
        const newFmt =
          r.new_loss_qty > 0
            ? `${r.new_loss_qty} pcs (${formatCents(r.new_loss_value_cents ?? 0)})`
            : "no loss";
        if (r.frozen_loss_state === "none" && r.new_loss_qty === 0) {
          return <Text type="secondary">no loss either way</Text>;
        }
        if (r.frozen_loss_state === "deleted_by_operator") {
          return (
            <span>
              <Tooltip title="The Stock-loss expense row was deleted post-finalize. Apply will recreate it with the new value.">
                <Tag color="red">deleted</Tag>
              </Tooltip>{" "}
              → <Text strong>{newFmt}</Text>
              {r.new_loss_qty > 0 && (
                <div>
                  <Text type="warning" style={{ fontSize: 12 }}>
                    will be recreated
                  </Text>
                </div>
              )}
            </span>
          );
        }
        if (r.frozen_loss_state === "operator_edited") {
          return (
            <span>
              <Tooltip title="You edited value_cents on this Stock-loss row manually. Apply will OVERWRITE your edit with the cascade-computed value.">
                <Tag color="orange">edited</Tag>
              </Tooltip>
              <Text type="secondary">
                {" "}
                {r.frozen_loss_qty} pcs ({formatCents(r.frozen_loss_value_cents ?? 0)})
              </Text>{" "}
              → <Text strong>{newFmt}</Text>
              <div>
                <Text type="danger" style={{ fontSize: 12 }}>
                  cascade will overwrite manual edit
                </Text>
              </div>
            </span>
          );
        }
        // AUTO or NONE
        const wasFmt =
          r.frozen_loss_qty > 0
            ? `${r.frozen_loss_qty} pcs (${formatCents(r.frozen_loss_value_cents ?? 0)})`
            : "no loss";
        return (
          <span>
            <Text type="secondary" delete>
              {wasFmt}
            </Text>{" "}
            → <Text strong>{newFmt}</Text>
          </span>
        );
      },
    },
  ];

  const disabledColumns: ColumnsType<ReanchorLineDiffRow> = [
    {
      title: "Article",
      dataIndex: "article_name",
      render: (v: string | null, r) => v ?? `#${r.chain_root_id}`,
    },
    {
      title: "Expected (snap → live)",
      key: "expected",
      width: 200,
      render: (_: unknown, r) =>
        `${r.frozen_expected_qty} → ${r.new_expected_qty}`,
    },
    {
      title: "Why locked",
      key: "reason",
      render: (_: unknown, r) => {
        if (r.blockers.includes("no_buy_price")) {
          return (
            <Text type="secondary">
              Missing buy_price snapshot — set it via the line patch first.
            </Text>
          );
        }
        if (!r.is_latest_for_root) {
          return (
            <Text type="secondary">
              Another finalized stocktake counts this article later. Re-anchor
              the most-recent stocktake instead.
            </Text>
          );
        }
        return <Text type="secondary">—</Text>;
      },
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        step === "warning"
          ? "Re-anchor frozen snapshot — read this first"
          : `Re-anchor preview — stocktake #${stocktakeId}`
      }
      width={step === "warning" ? 560 : 1000}
      destroyOnHidden
      footer={
        step === "warning" ? (
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" loading={loading} onClick={() => void loadPreview()}>
              I understand — show me the diff
            </Button>
          </Space>
        ) : step === "preview" ? (
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              danger
              disabled={!preview || preview.stale_lines.length === 0}
              onClick={() => void apply()}
            >
              Apply re-anchor
              {preview && preview.stale_lines.length > 0 && (
                <>
                  {" "}({preview.stale_lines.length - skipped.size}/{preview.stale_lines.length})
                </>
              )}
            </Button>
          </Space>
        ) : (
          <Button disabled>Applying…</Button>
        )
      }
    >
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 12 }}
        />
      )}
      {step === "warning" && (
        <div>
          <Alert
            type="warning"
            showIcon
            message="Re-anchoring rewrites a frozen audit snapshot"
            description={
              <div>
                <p style={{ marginBottom: 8 }}>
                  This action updates <code>expected_qty_at_finalize</code>{" "}
                  for selected lines to match what live stock state SAYS
                  it should be at this stocktake's counted moment — using
                  TODAY's corrected procurement, adjustments and sales.
                  The cascade then re-derives every linked
                  <code> stock_adjustment</code> + Stock-loss expense
                  using the unchanged counted_qty.
                </p>
                <p style={{ marginBottom: 8 }}>
                  Use this when you've corrected a procurement entry,
                  re-categorised an adjustment, or had to undelete /
                  re-insert a strichliste transaction after finalize.
                </p>
                <p style={{ marginBottom: 0 }}>
                  Audit log captures the before / after of every line
                  touched. Re-anchor is gated to the most-recent
                  finalized stocktake per article — older stocktakes
                  surface as "locked".
                </p>
              </div>
            }
            style={{ marginBottom: 12 }}
          />
        </div>
      )}
      {step !== "warning" && preview && (
        <div>
          {preview.stale_lines.length === 0 && (
            <Empty description="Nothing to re-anchor — every line's frozen Expected matches today's live values." />
          )}
          {preview.stale_lines.length > 0 && (
            <Table
              size="small"
              rowKey="chain_root_id"
              dataSource={preview.stale_lines}
              columns={previewColumns}
              pagination={false}
              style={{ marginBottom: 12 }}
            />
          )}
          {(preview.blocked_lines.length > 0 || preview.locked_lines.length > 0) && (
            <div style={{ marginTop: 12 }}>
              <Title level={5}>Cannot re-anchor</Title>
              <Table
                size="small"
                rowKey="chain_root_id"
                dataSource={[
                  ...preview.blocked_lines,
                  ...preview.locked_lines,
                ]}
                columns={disabledColumns}
                pagination={false}
              />
            </div>
          )}
        </div>
      )}
      {step === "applying" && (
        <div style={{ padding: 24, textAlign: "center" }}>
          <Spin />
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">Applying re-anchor…</Text>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default StocktakeDetailPage;
